import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

export type Lang = "vi" | "en" | "zh";

const LANG_KEY = "acc.lang";

/** Tiếng Việt là nguồn chuẩn — mọi ngôn ngữ khác phải có đủ key của bảng này. */
const vi = {
  "app.name": "Auto Check",
  "app.cms": "CMS",
  "app.tagline": "Hệ thống giám sát link đa thiết bị",
  "app.booting": "Đang khởi động…",

  "common.loading": "Đang tải…",
  "common.close": "Đóng",
  "common.cancel": "Huỷ",
  "common.save": "Lưu",
  "common.refresh": "Làm mới",
  "common.edit": "Sửa",
  "common.delete": "Xoá",
  "common.details": "Chi tiết",
  "common.copy": "Sao chép",
  "common.copied": "Đã chép",
  "common.all": "Tất cả",
  "common.optional": "Tuỳ chọn",
  "common.language": "Ngôn ngữ",
  "common.logout": "Đăng xuất",
  "country.label": "Quốc gia",
  "country.switchHint": "Chuyển quốc gia đang thao tác — dữ liệu user, brand và link đều tách riêng",
  "country.lockedHint": "Tài khoản của bạn chỉ thao tác trong quốc gia này",
  "country.allCountries": "Mọi quốc gia",
  "country.vn": "Việt Nam",
  "country.br": "Brazil",


  "status.healthy": "Hoạt động",
  "status.unhealthy": "Lỗi",
  "status.unknown": "Chưa rõ",

  "time.justNow": "vừa xong",
  "time.minutes": "{n} phút trước",
  "time.hours": "{n} giờ trước",
  "time.days": "{n} ngày trước",

  "nav.dashboard": "Tổng quan",
  "nav.links": "Quản lý link",
  "nav.checks": "Kết quả kiểm tra",
  "nav.devices": "Thiết bị",
  "nav.reports": "Báo cáo",
  "nav.guide": "Hướng dẫn sử dụng",
  "nav.channels": "Kênh thông báo",
  "nav.users": "Người dùng",
  "nav.brands": "Brand",
  "nav.appconfig": "Phiên bản app",
  "nav.roles": "Vai trò & Phân quyền",
  "nav.iprules": "Chặn IP CMS",
  "nav.logs": "Log check domain",
  "nav.adminlogs": "Nhật ký Admin",
  "nav.openapi": "Kết nối OpenAPI",

  "guide.title": "Hướng dẫn sử dụng",
  "guide.subtitle": "Tài liệu chi tiết hướng dẫn thao tác trên CMS và kết nối thiết bị",
  "guide.search": "Tìm kiếm nội dung hướng dẫn...",
  "guide.toc": "Mục lục",
  "guide.noResults": "Không tìm thấy nội dung khớp với từ khoá tìm kiếm.",
  "openapi.title": "Kết nối OpenAPI",
  "openapi.subtitle": "Cổng đặc tả kỹ thuật OpenAPI 3.0, Swagger UI tương tác và mã nguồn mẫu tích hợp",
  "openapi.superadminBadge": "Chỉ dành riêng cho Superadmin",
  "openapi.openNewTab": "Mở tab mới",
  "openapi.fullscreen": "Toàn màn hình",
  "openapi.reload": "Tải lại",
  "openapi.copied": "Đã sao chép!",
  "openapi.copyToken": "Sao chép Token",
  "openapi.specFiles": "Tài liệu OpenAPI Spec (YAML)",
  "openapi.authHeader": "Xác thực API",
  "openapi.codeExamples": "Ví dụ tích hợp code",
  "openapi.postmanTitle": "Nhập vào Postman (Postman Collection)",
  "openapi.postmanDesc": "Dễ dàng kiểm thử & gọi API bằng Postman qua link trực tiếp hoặc file Collection JSON đã cấu hình sẵn",
  "openapi.postmanStep1": "Cách 1 — Nhập URL trực tiếp vào Postman (Khuyên dùng)",
  "openapi.postmanStep1Desc": "Sao chép link OpenAPI hoặc Collection ở dưới, mở Postman -> bấm Import (Ctrl/Cmd + O) -> dán link vào.",
  "openapi.postmanStep2": "Cách 2 — Tải file JSON Collection",
  "openapi.postmanStep2Desc": "Tải file JSON bên dưới về máy, mở Postman -> bấm Import -> kéo thả file vào là xong.",
  "openapi.deniedTitle": "Quyền truy cập bị từ chối",
  "openapi.deniedDesc": "Tính năng kết nối OpenAPI chỉ dành riêng cho tài khoản Superadmin. Vui lòng liên hệ quản trị viên tối cao nếu bạn cần tích hợp API.",
  "openapi.authDesc": "Tất cả các route quản trị CMS yêu cầu Bearer JWT Token của Superadmin. Thiết bị di động sử dụng Device Key.",
  "openapi.activeToken": "TOKEN SUPERADMIN HIỆN TẠI:",
  "openapi.sampleHeaders": "HEADER MẪU:",
  "openapi.specDesc": "Tải file đặc tả kỹ thuật OpenAPI dạng YAML để nạp vào Postman, Insomnia, Swagger Editor hoặc sinh client SDK tự động.",
  "openapi.downloadYaml": "Tải về / Mở file YAML",
  "openapi.downloadJson": "Tải file JSON về máy",
  "openapi.copyUrl": "Sao chép URL",
  "openapi.copyPostmanUrl": "Sao chép Link để dán vào Postman Import",
  "openapi.copySnippet": "Sao chép đoạn mã",
  "openapi.exitFullscreen": "Thoát toàn màn hình",
  "openapi.postmanGuideTitle": "HƯỚNG DẪN IMPORT VÀO POSTMAN:",
  "openapi.postmanStep1Title": "Mở Postman & Bấm nút \"Import\"",
  "openapi.postmanStep1Text": "Ở góc trên bên trái Postman, bấm Import (hoặc nhấn Ctrl + O / Cmd + O).",
  "openapi.postmanStep2Title": "Dán URL hoặc Kéo thả file",
  "openapi.postmanStep2Text": "Bạn có thể dán trực tiếp link URL của file YAML / JSON Collection hoặc kéo thả file vừa tải về. Postman hỗ trợ cả OpenAPI 3.0 YAML và Postman v2.1 JSON.",
  "openapi.postmanStep3Title": "Điền Token & Base URL",
  "openapi.postmanStep3Text": "Trong Postman, thiết lập biến baseUrl và sao chép Superadmin Bearer Token ở trên vào tab Authorization của Collection.",
  "guide.backToTop": "Lên đầu trang",
  "roles.descPlaceholder": "e.g. Giám sát tình trạng link",
  "roles.allPermsHint": "Bao gồm toàn bộ quyền hiện tại và mọi quyền được thêm mới trong tương lai",
  "iprules.commentPlaceholder": "e.g. Chặn IP spam link hoặc cho phép IP văn phòng",

  "users.title": "Người dùng",
  "users.subtitle": "Quản lý tài khoản & gán brand. Superadmin thấy mọi brand; user brand chỉ thấy link brand mình.",
  "users.add": "Thêm người dùng",
  "users.username": "Tên đăng nhập",
  "users.usernameHint": "Tối thiểu 3 ký tự, không trùng",
  "users.password": "Mật khẩu",
  "users.passwordHint": "Tối thiểu 6 ký tự",
  "users.newPassword": "Mật khẩu mới",
  "users.role": "Vai trò",
  "users.roleHint": "superadmin: toàn quyền; admin/brand: khoá theo brand",
  "users.role.superadmin": "Superadmin",
  "users.role.admin": "Admin",
  "users.role.brand": "Brand",
  "users.brand": "Brand",
  "users.brandHint": "Brand mà tài khoản này quản lý (khớp brand của link)",
  "users.brandNa": "Superadmin quản mọi brand — không cần gán",
  "users.allBrands": "Mọi brand",
  "users.noBrand": "Chưa gán brand",
  "users.locked": "Đã khoá",
  "users.active": "Hoạt động",
  "users.lock": "Khoá",
  "users.unlock": "Mở khoá",
  "users.lockAccount": "Khoá tài khoản này (không đăng nhập được)",
  "users.created": "Ngày tạo",
  "users.you": "bạn",
  "users.formNew": "Thêm người dùng",
  "users.formEdit": "Sửa người dùng \"{name}\"",
  "users.create": "Tạo",
  "users.resetPw": "Đặt lại mật khẩu",
  "users.resetPwTitle": "Đặt lại mật khẩu — {name}",
  "users.resetPwOk": "Đã đổi mật khẩu cho \"{name}\".",

  "brands.title": "Brand",
  "brands.subtitle": "Danh mục brand để gán cho user & link. Đổi tên brand sẽ cập nhật mọi link/user đang dùng.",
  "brands.add": "Thêm brand",
  "brands.name": "Mã brand",
  "brands.nameHint": "Chữ, số, . _ - (không khoảng trắng), vd: demo, acme",
  "brands.note": "Ghi chú",
  "brands.noteHint": "Tên đầy đủ / mô tả (tuỳ chọn)",
  "brands.choose": "— Chọn brand —",
  "brands.new": "Brand mới",
  "brands.add2": "Thêm",
  "brands.namePlaceholder": "Mã brand mới…",
  "brands.links": "Số link",
  "brands.users": "Số user",
  "brands.created": "Ngày tạo",
  "brands.formNew": "Thêm brand",
  "brands.formEdit": "Sửa brand \"{name}\"",
  "brands.create": "Tạo",
  "brands.confirmDelete": "Xoá brand \"{name}\"?",

  "chan.title": "Kênh thông báo",
  "chan.subtitle": "{on}/{total} kênh đang bật · gửi cảnh báo qua Telegram, Lark",
  "chan.add": "Thêm kênh",
  "chan.guide": "Hướng dẫn",
  "chan.guideHint": "Chưa biết lấy token/webhook ở đâu? Xem hướng dẫn từng bước.",
  "chan.formNew": "Thêm kênh thông báo",
  "chan.formEdit": "Sửa kênh #{id}",
  "chan.name": "Tên kênh",
  "chan.nameHint": "Tên gợi nhớ, vd \"Telegram - Trực page\"",
  "chan.provider": "Nền tảng",
  "chan.providerHint": "Cả hai đều gửi vào NHÓM",
  "chan.brand": "Brand",
  "chan.brandHint": "Chỉ push link thuộc brand này (mặc định: mọi brand)",
  "chan.brandAll": "Mọi brand",
  "chan.language": "Ngôn ngữ tin nhắn",
  "chan.languageHint": "Bot sẽ gửi cảnh báo vào nhóm này bằng ngôn ngữ đã chọn (không phụ thuộc ngôn ngữ CMS)",
  "chan.col.language": "Ngôn ngữ",
  "chan.provider.telegram": "Telegram",
  "chan.provider.lark_webhook": "Lark (webhook nhóm)",
  "chan.provider.lark": "Lark (nhóm)",
  "chan.provider.lark_app": "Lark (app → user/chat)",
  "chan.targetType": "Loại đích",
  "chan.targetTypeHint": "Chỉ để nhận diện, không đổi cách gửi",
  "chan.target.group": "Nhóm",
  "chan.target.user": "Người dùng",
  "chan.target.channel": "Kênh/Channel",
  "chan.target.none": "— Không đặt —",
  "chan.col.target": "Đích",
  "chan.col.throttle": "Chống spam",
  "chan.throttleHint": "Cùng một link chỉ gửi lại sau ngần này phút (0 = gửi mọi lần)",
  "chan.minutes": "{n} phút",
  "chan.noThrottle": "Gửi mọi lần",
  "chan.notifyUnknown": "Gửi cả trạng thái \"nghi bị chặn\"",
  "chan.notifyUnknownHint": "Bật: gửi cả link unknown (WAF/bot chặn). Tắt: chỉ gửi link lỗi rõ (unhealthy).",
  "chan.enabled": "Bật kênh này",
  "chan.enable": "Bật",
  "chan.disable": "Tắt",
  "chan.status.on": "Đang bật",
  "chan.status.off": "Đã tắt",
  "chan.save": "Lưu",
  "chan.create": "Tạo kênh",
  "chan.country": "Quốc gia",
  "chan.countryHint": "Kênh chỉ nhận cảnh báo từ link/thiết bị của quốc gia này",
  "chan.countryFilter": "Quốc gia",
  "chan.countryAll": "Tất cả quốc gia",
  "chan.test": "Gửi thử",
  "chan.testError": "Gửi thử log lỗi thiết bị",
  "chan.testing": "Đang gửi thử tới \"{name}\"…",
  "chan.testingError": "Đang gửi thử log lỗi thiết bị tới \"{name}\"…",
  "chan.testOk": "Đã gửi tin thử tới \"{name}\" thành công.",
  "chan.testErrorOk": "Đã gửi thử log lỗi thiết bị tới \"{name}\" thành công.",
  "chan.clone": "Sao chép sang nước khác",
  "chan.cloneTitle": "Sao chép kênh sang quốc gia khác",
  "chan.cloneSubtitle": "Sao chép nguyên vẹn Bot Token / App Secret bí mật của kênh này mà không cần nhập lại token.",
  "chan.cloneSource": "Kênh nguồn",
  "chan.cloneTargetCountry": "Sao chép sang quốc gia",
  "chan.cloneName": "Tên kênh mới",
  "chan.cloneChatId": "Chat ID mới (của quốc gia này)",
  "chan.cloneChatIdHint": "ID nhóm Telegram (vd -100...) hoặc Chat ID Lark (oc_...)",
  "chan.cloneThreadId": "Topic / Thread ID (tuỳ chọn)",
  "chan.cloneThreadIdHint": "Nếu nhóm Telegram đích bật chủ đề (topics)",
  "chan.cloneTip": "Token/Secret của bot được giữ nguyên tự động — chỉ cần đổi Chat ID hoặc Topic ID phù hợp với nhóm của nước đích.",
  "chan.cloneBtn": "Sao chép & Tạo kênh",
  "chan.cloneSuccess": "Đã sao chép kênh sang {country} thành công.",
  "chan.testFail": "Gửi thử thất bại: {error}",
  "chan.delete": "Xoá",
  "chan.confirmDelete": "Xoá kênh \"{name}\"?",
  "chan.lastSent": "Gửi gần nhất",
  "chan.never": "Chưa gửi",
  "chan.secretKeep": "Để trống = giữ giá trị đã lưu",
  "chan.tg.botToken": "Bot token",
  "chan.tg.botTokenHint": "Lấy từ @BotFather",
  "chan.tg.chatId": "Chat ID",
  "chan.tg.chatIdHint": "ID nhóm/user (vd -1001234567890). Thêm bot vào nhóm trước.",
  "chan.tg.threadId": "Thread ID (tuỳ chọn)",
  "chan.tg.threadIdHint": "Nếu nhóm bật chủ đề (topics)",
  "chan.tg.sharedBotTip": "Mẹo: Có thể dùng chung 1 Bot Token cho cả VN và Brazil bằng cách tạo 2 kênh (1 kênh VN, 1 kênh BR) với Chat ID hoặc Topic/Thread ID riêng.",
  "chan.lw.webhookUrl": "Webhook URL",
  "chan.lw.webhookUrlHint": "URL bot tuỳ chỉnh trong nhóm Lark",
  "chan.lw.secret": "Secret ký (tuỳ chọn)",
  "chan.lw.secretHint": "Nếu bot bật xác thực bằng chữ ký",
  "chan.lk.domain": "Máy chủ",
  "chan.lk.domainHint": "Lark quốc tế hoặc Feishu (TQ)",
  "chan.lk.chatId": "Chat ID nhóm",
  "chan.lk.chatIdHint": "ID nhóm dạng oc_… (thêm bot vào nhóm rồi lấy)",
  "chan.lk.appId": "App ID",
  "chan.lk.appIdHint": "cli_… từ Lark Developer Console",
  "chan.lk.appSecret": "App Secret",
  "chan.lk.chatIdLocked": "Nhập App ID + App Secret trước để chọn nhóm",
  "chan.lk.chooseGroup": "— Chọn nhóm —",
  "chan.lk.loading": "Đang tải danh sách nhóm…",
  "chan.lk.noChats": "Bot chưa ở nhóm nào — thêm bot vào nhóm rồi thử lại",
  "chan.lk.mode": "Nhóm nhận cảnh báo",
  "chan.lk.modeBroadcast": "Mọi nhóm bot đang ở (khuyến nghị)",
  "chan.lk.modeChat": "Một nhóm cố định",
  "chan.lk.modeBroadcastHint":
    "Chỉ cần thêm bot vào nhóm là nhóm đó nhận được — không phải khai chat_id. Cần app có quyền im:chat:readonly.",
  "chan.lk.modeChatHint": "Gửi đúng một nhóm — chọn theo tên ở ô bên dưới",
  "chan.lk.allGroups": "Mọi nhóm",
  "chan.lk.preview": "Xem trước nhóm nhận",
  "chan.lk.previewHint": "Danh sách nhóm bot đang ở — tự cập nhật mỗi lần gửi",
  "chan.lk.previewBtn": "Tải danh sách nhóm",
  "chan.lk.previewCount": "{n} nhóm sẽ nhận cảnh báo",
  "chan.lk.testBroadcastConfirm":
    "Kênh \"{name}\" đang bật chế độ mọi nhóm — tin nhắn thử sẽ gửi tới TẤT CẢ nhóm bot đang ở. Tiếp tục?",
  "chan.la.domain": "Máy chủ",
  "chan.la.domainHint": "Lark quốc tế hoặc Feishu (TQ)",
  "chan.la.appId": "App ID",
  "chan.la.appSecret": "App Secret",
  "chan.la.receiveIdType": "Loại ID nhận",
  "chan.la.receiveId": "ID người/chat nhận",
  "chan.la.receiveIdHint": "Giá trị khớp loại ID đã chọn (open_id, chat_id…)",

  "login.username": "Tên đăng nhập",
  "login.password": "Mật khẩu",
  "login.submit": "Đăng nhập",
  "login.welcome": "Chào mừng trở lại",
  "login.subtitle": "Đăng nhập để quản lý hệ thống giám sát link",
  "login.hero": "Giám sát sức khoẻ link, biết ngay link chết thật hay bị chặn theo vùng",
  "login.f1t": "Giám sát đa nguồn",
  "login.f1d": "Kiểm tra từ server và nhiều thiết bị mobile trên mạng thật",
  "login.f2t": "Phát hiện chặn theo khu vực",
  "login.f2d": "Phân biệt link chết thật với link chỉ bị chặn theo mạng/vùng",
  "login.f3t": "Cảnh báo tức thì",
  "login.f3d": "Đẩy thông báo qua Telegram và Lark ngay khi link gặp vấn đề",

  "table.brand": "Brand",
  "table.domain": "Domain",
  "table.status": "Trạng thái",
  "table.http": "HTTP",
  "table.response": "Phản hồi",
  "table.error": "Lỗi",
  "table.deviceHint": "Thiết bị check gần đây nhất",
  "table.checkerIp": "IP đã check",
  "table.checkerIpHint": "IP đã đi kiểm tra link — chính IP này bị chặn khi lỗi là 403/429",
  "table.checkedAt": "Kiểm tra lúc",
  "table.note": "Ghi chú",

  // Câu mô tả lỗi check — server chỉ gửi mã lỗi, câu chữ dựng ở đây
  "check.err.blocked": "Bị chặn (HTTP {status}) — nhiều khả năng WAF/bot protection, chưa kết luận được link sống hay chết",
  "check.err.http": "HTTP {status}",
  "check.err.timeout": "Quá thời gian chờ sau {detail}ms",
  "check.err.network": "Lỗi mạng: {detail}",
  "check.err.unknown": "Lỗi không xác định",
  "table.expiry": "Hết hạn",
  "table.owner": "Chủ sở hữu",
  "table.country": "Quốc gia",
  "table.source": "Nguồn",
  "table.device": "Thiết bị",
  "table.region": "Khu vực",
  "table.network": "Mạng",
  "table.ip": "IP",
  "table.time": "Thời điểm",

  "notif.title": "Thông báo",
  "notif.empty": "Chưa có thông báo nào.",
  "notif.markAllRead": "Đánh dấu đã đọc",
  "notif.toastTitle": "Link kiểm tra bị lỗi",

  "dash.title": "Tổng quan",
  "dash.subtitle": "Tình trạng toàn hệ thống theo lần kiểm tra gần nhất",
  // Nút dùng khoá này đang được comment (BrandLinks.tsx / Dashboard.tsx)
  "dash.checkAll": "Kiểm tra tất cả",
  "dash.generateReport": "Sinh báo cáo",
  "dash.totalLinks": "Tổng link",
  "dash.activeDevices": "Thiết bị hoạt động",
  "dash.attention": "Cần chú ý",
  "dash.viewAllLinks": "Xem tất cả link →",
  "dash.allHealthy": "Tất cả link đều hoạt động bình thường 🎉",
  "dash.latestReport": "Báo cáo mới nhất",
  "dash.viewHistory": "Lịch sử →",
  "dash.noReport": "Chưa có báo cáo nào.",
  "dash.generatedAt": "Sinh lúc",
  "dash.duration": "Thời gian chạy",
  "dash.ratio": "Tổng / Khoẻ / Lỗi",

  "trigger.cron": "Tự động",
  "trigger.manual": "Thủ công",

  "links.title": "Quản lý link",
  "links.subtitle": "{n} link đang được giám sát",
  "links.subtitleDisabled": "{n} link đã bị disable",
  "links.exportCsv": "Xuất CSV",
  "links.exportJson": "Xuất JSON",
  "links.import": "Nhập hàng loạt",
  "links.add": "+ Thêm link",
  "links.search": "Tìm theo brand hoặc domain…",
  "links.check": "Kiểm tra",
  "links.compare": "So sánh",
  "links.disable": "Vô hiệu hoá",
  "links.confirmDelete": 'Vô hiệu hoá link "{domain}" của brand {brand}? Link không bị xoá — lịch sử kiểm tra vẫn giữ, có thể khôi phục lại ở tab "Đã disable".',
  "links.emptyFiltered": "Không có link nào khớp bộ lọc.",
  "links.empty": "Chưa có link nào — thêm link đầu tiên.",
  "links.formNew": "Thêm link mới",
  "links.formEdit": "Sửa link #{id}",
  "links.domainHint": "URL đầy đủ, ví dụ https://example.com",
  "links.note": "Ghi chú",
  "links.noteHint": "Ghi chú tuỳ chọn về link (mục đích, chiến dịch...)",
  "links.notePlaceholder": "Ví dụ: Link chạy ads, link đối tác...",
  "links.expiryLabel": "Ngày hết hạn",
  "links.create": "Tạo link",
  "links.save": "Lưu thay đổi",
  "links.onlyMine": "Link của tôi",
  "links.onlyMineHint": "Chỉ hiện link do tôi tạo",
  "links.ownerShared": "Chung brand",
  "links.ownerSharedHint": "Link chưa gắn chủ — mọi user trong brand đều sửa được",
  "links.ownerMe": "bạn",
  "links.ownerHint": "Người được phép sửa/xoá link này",
  "links.ownerReadonlyHint": "Chỉ superadmin mới đổi được chủ sở hữu",
  "links.notOwner": "Link của user khác",
  "links.bulkSelected": "Đã chọn {n} link",
  "links.bulkClear": "Bỏ chọn",
  "links.bulkOwner": "Đổi chủ sở hữu",
  "links.bulkOwnerTitle": "Đổi chủ sở hữu cho {n} link",
  "links.bulkOwnerHint": "Chủ mới phải cùng brand với link — link khác brand sẽ bị bỏ qua",
  "links.bulkApply": "Cập nhật",
  "links.bulkDone": "Đã đổi chủ {updated}/{requested} link",
  "links.bulkErrorRow": "Link #{id}: {error}",
  "links.bulkDelete": "Vô hiệu hoá đã chọn",
  "links.bulkDeleteTitle": "Vô hiệu hoá {n} link",
  "links.bulkDeleteConfirm": "Vô hiệu hoá {n} link đã chọn? Link không bị xoá, lịch sử kiểm tra vẫn được giữ và có thể khôi phục lại sau. Bạn chỉ vô hiệu hoá được link của mình, trừ khi là quản trị viên.",
  "links.bulkDeleteApply": "Vô hiệu hoá",
  "links.bulkDeleteDone": "Đã xoá {disabled}/{requested} link",
  "links.viewActive": "Đang hoạt động",
  "links.viewDisabled": "Đã disable",
  "links.restore": "Khôi phục",
  "links.confirmRestore": 'Khôi phục link "{domain}" của brand {brand}? Link sẽ được check lại bình thường.',
  "links.disabledHint": "Các link này đã bị disable — không hiện trong danh sách check, thiết bị không nhận, và không thể kiểm tra tay cho tới khi khôi phục.",
  "links.auditHistory": "Lịch sử Audit",
  "links.viewList": "Danh sách",
  "links.viewMatrix": "Ma trận",
  "audit.modalTitle": "Lịch sử Audit Link",
  "audit.uptimeRate": "Tỷ lệ sống",
  "audit.avgLatency": "Độ trễ trung bình",
  "audit.totalChecks": "Lượt kiểm tra",
  "audit.currentConsensus": "Trạng thái tức thời theo từng nguồn",
  "audit.tabMatrix": "Ma trận lịch sử",
  "audit.tabList": "Danh sách chi tiết",
  "audit.tabAdminLogs": "Nhật ký Admin",
  "audit.filterSource": "Nguồn",
  "audit.limit": "bản ghi",
  "audit.roundTime": "Thời gian kiểm tra",
  "audit.consensus": "Đồng thuận",
  "audit.clusterNote": "Mỗi hàng là một đợt kiểm tra đồng thời trên các nguồn (gom nhóm trong vòng 90s). Bấm vào ô để xem chi tiết.",
  "audit.noData": "Chưa có dữ liệu kiểm tra nào cho link này.",
  "audit.noAdminLogs": "Chưa có nhật ký thao tác admin nào cho link này.",
  "audit.detailTitle": "Chi tiết kết quả kiểm tra",

  "import.title": "Nhập link hàng loạt",
  "import.tabFile": "Tải file",
  "import.tabCsv": "Dán danh sách",
  "import.submit": "Nhập dữ liệu",
  "import.fileLabel": "File dữ liệu",
  "import.fileHint": "Hỗ trợ .xlsx, .xls, .csv, .json — chỉ liệt kê domain, hoặc các cột brand, domain, note",
  "import.csvLabel": "Danh sách domain",
  "import.csvHint": "Mỗi dòng một domain, hoặc nhiều domain cách nhau bằng dấu phẩy/khoảng trắng. Muốn mỗi dòng một brand/ghi chú riêng thì ghi kiểu brand,domain,note hoặc domain,note.",
  "import.noFile": "Chưa chọn file.",
  "import.noDomains": "Chưa dán domain nào.",
  "import.brandHint": "Brand gán cho mọi dòng không tự ghi brand — dán danh sách URL trần thì chọn ở đây",
  "import.brandFromData": "Theo cột brand trong dữ liệu",
  "import.noteLabel": "Ghi chú mặc định",
  "import.noteHint": "Ghi chú áp dụng cho các link được nhập nếu trong dữ liệu chưa có",
  "import.notePlaceholder": "Ví dụ: Nhập đợt 1, link dự phòng...",
  "import.modeLabel": "Xử lý trùng (brand + domain)",
  "import.modeUpsert": "Upsert — cập nhật bản ghi trùng",
  "import.modeSkip": "Skip — bỏ qua bản ghi trùng",
  "import.received": "Nhận",
  "import.created": "Tạo mới",
  "import.updated": "Cập nhật",
  "import.skipped": "Bỏ qua",
  "import.failed": "Lỗi",
  "import.errorRow": "Dòng {row}: {error}",

  "devices.title": "Thiết bị",
  "devices.subtitle": "{active}/{total} thiết bị đang hoạt động",
  "devices.add": "+ Đăng ký thiết bị",
  "devices.empty": "Chưa có thiết bị nào. Đăng ký thiết bị để nhận API key cho app mobile.",
  "devices.apiKey": "API key",
  "devices.platform": "Nền tảng",
  "devices.carrier": "Nhà mạng",
  "devices.appVersion": "Phiên bản app",
  "devices.model": "Model",
  "devices.lastSeen": "Lần cuối online",
  "devices.locked": "Đã khoá",
  "devices.lock": "Khoá",
  "devices.unlock": "Mở khoá",
  "devices.history": "Lịch sử",
  "devices.rotate": "Cấp key mới",
  "devices.confirmRotate": 'Cấp lại API key cho "{name}"? Key cũ bị thu hồi ngay lập tức.',
  "devices.confirmDelete": 'Xoá thiết bị "{name}"? Thao tác này không hoàn tác được.',
  "devices.formNew": "Đăng ký thiết bị mới",
  "devices.formEdit": "Sửa thiết bị #{id}",
  "devices.nameLabel": "Tên thiết bị",
  "devices.nameHint": "Ví dụ Pixel-HN-01",
  "devices.networkHint": "wifi / 4g / 5g",
  "devices.regionHint": "Ví dụ Hà Nội",
  "devices.register": "Đăng ký",
  "devices.save": "Lưu thay đổi",
  "devices.keyTitle": "API key thiết bị",
  "devices.keyWarn":
    "⚠️ Key chỉ hiển thị một lần duy nhất. Sao chép và nạp vào app ngay — đóng cửa sổ này là không xem lại được.",
  "devices.keyDevice": "Thiết bị:",
  "devices.keyCopy": "Sao chép",
  "devices.keyCopied": "Đã chép ✓",
  "devices.keyAck": "Tôi đã lưu key",
  "devices.keyHint": "App gửi key qua header X-Device-Key khi gọi /device-api/*.",

  "devices.state.online": "Online",
  "devices.state.offline": "Offline",
  "devices.state.disabled": "Đã tắt",
  "devices.onlineHint": "Liên lạc trong {n} phút gần đây",
  "devices.results": "Kết quả",
  "devices.resultsTitle": "Lịch sử kiểm tra — {name}",
  "devices.resultsEmpty": "Máy này chưa gửi kết quả nào.",
  "devices.quickAdd": "+ Thiết bị",

  "matrix.title": "Ma trận link × nguồn",
  "matrix.subtitle": "Kết quả mới nhất của từng nguồn, kèm mức đồng thuận",
  "matrix.link": "Link",
  "matrix.verdict": "Kết luận",
  "matrix.onlyProblems": "Chỉ hiện link có vấn đề",
  "matrix.empty": "Chưa có dữ liệu ma trận.",
  "matrix.emptyFiltered": "Không có link nào đang gặp vấn đề 🎉",
  "matrix.noColumns": "Chưa có nguồn kiểm tra nào. Đăng ký thiết bị để bắt đầu so sánh.",
  "matrix.staleNote": "Ô mờ = kết quả cũ hơn {n} phút · dấu · = nguồn chưa từng gửi kết quả",
  "matrix.noResultsHint":
    "Chưa nguồn nào gửi kết quả cho {n} link — nên toàn bộ đang là “Chưa rõ”. Kiểm tra xem app trên thiết bị đã chạy và đã gọi POST /device-api/results chưa.",
  "matrix.staleLabel": "Số phút coi là cũ",
  "matrix.failingRegions": "Khu vực lỗi",
  "matrix.failingNetworks": "Mạng lỗi",
  "matrix.avgResponse": "Phản hồi trung bình",
  "matrix.missing": "Chưa có kết quả",
  "matrix.stale": "Kết quả cũ",
  "matrix.serverColumn": "Server",

  "verdict.healthy": "Bình thường",
  "verdict.partial": "Chặn cục bộ",
  "verdict.down": "Chết thật",
  "verdict.unknown": "Chưa rõ",
  "verdict.partialHint": "Chỉ một số nguồn lỗi — nghi bị chặn theo mạng/khu vực",
  "verdict.downHint": "Mọi nguồn đều lỗi — link chết thật",

  "reports.title": "Báo cáo",
  "reports.subtitle": "Snapshot sinh thủ công qua nút bên phải",
  "reports.generate": "Sinh báo cáo ngay",
  "reports.empty": "Chưa có báo cáo nào.",
  "reports.total": "Tổng",
  "reports.ratio": "Tỷ lệ",
  "reports.duration": "Thời gian chạy",
  "reports.detailTitle": "Báo cáo #{id} — {date}",
  "reports.onlyFailed": "Chỉ hiện link có vấn đề",
  "reports.noDetails": "Không có link nào trong mục này.",

  "checks.title": "Kết quả kiểm tra",
  "checks.subtitle":
    "So sánh cùng một link giữa các thiết bị, khu vực và mạng — kèm góc nhìn từ server",
  "checks.tabCompare": "So sánh mới nhất",
  "checks.tabHistory": "Lịch sử đầy đủ",
  "checks.link": "Link",
  "checks.allLinks": "Tất cả link",
  "checks.allDevices": "Tất cả thiết bị",
  "checks.limit": "Số bản ghi",
  "checks.regionPlaceholder": "Ví dụ Hà Nội",
  "checks.emptyFiltered": "Không có kết quả nào khớp bộ lọc.",
  "checks.emptyCompare":
    "Chưa có kết quả nào từ thiết bị. Đăng ký thiết bị và để app gửi kết quả về.",
  "checks.conflictWarn":
    "⚠️ {n} link có kết quả không đồng nhất giữa các agent — nhiều khả năng bị chặn theo mạng hoặc khu vực.",
  "checks.conflict": "Không đồng nhất",
  "checks.sourceServer": "Server",
  "checks.sourceDevice": "Thiết bị",
  "checks.clear": "Xoá lịch sử",
  "checks.clearTitle": "Xoá toàn bộ lịch sử kiểm tra",
  "checks.clearWarn":
    "Thao tác này xoá vĩnh viễn TOÀN BỘ lịch sử kiểm tra của MỌI brand (cả server lẫn thiết bị) và không thể hoàn tác.",
  "checks.clearKeeps":
    "Trạng thái mới nhất của từng link, báo cáo và thông báo vẫn được giữ — chỉ bảng lịch sử chi tiết bị xoá. Sau khi xoá, trang này và ma trận sẽ trống cho tới lần check tiếp theo.",
  "checks.clearPromptLabel": "Gõ “{word}” để xác nhận",
  "checks.clearConfirmAction": "Xoá vĩnh viễn",
  "checks.clearDone": "Đã xoá {n} bản ghi lịch sử.",
  "checks.sendAlert": "Gửi log lỗi ra Bot",
  "checks.sendAlertTitle": "Gửi log lỗi thiết bị ra Bot",
  "checks.sendAlertSubtitle": "Chủ động quét các lỗi gần nhất của thiết bị check trong khu vực và đẩy ra bot Telegram/Lark.",
  "checks.sendAlertBtn": "Bắn thông báo ngay",
  "checks.sendAlertHours": "Khoảng thời gian lỗi",
  "checks.sendAlertCountry": "Khu vực / Quốc gia",
  "checks.sendAlertDevice": "Thiết bị",
  "checks.sendAlertSuccess": "Đã gửi thông báo lỗi thiết bị thành công.",

  "logs.title": "Log check domain",
  "logs.subtitle":
    "Log ghi lại từ Domain Check Logger API — một service riêng, tách khỏi hệ thống check link chính. Kèm log các lần forward sự kiện lỗi sang hệ thống thứ 3.",
  "logs.notConfigured":
    "Chưa cấu hình kết nối tới Domain Check Logger API (thiếu BUN_PUBLIC_LOGS_API_BASE / _KEY / _SECRET lúc build).",
  "logs.location": "Vị trí",
  "logs.result": "Kết quả",
  "logs.resultAll": "Tất cả kết quả",
  "logs.resultSuccess": "Thành công",
  "logs.resultError": "Lỗi",
  "logs.brandHint": "Chỉ áp dụng nếu key đang dùng là master key — key thường luôn bị khoá về 1 brand",
  "logs.cmsName": "CMS nguồn",
  "logs.receivedAt": "Ghi log lúc",
  "logs.checkedAt": "Client check lúc",
  "logs.geo": "Vị trí (geo)",
  "logs.geoNone": "Không có dữ liệu geo",
  "logs.empty": "Không có log nào khớp bộ lọc.",
  "logs.totalCount": "{n} bản ghi",
  "logs.thirdParty":
    "Log lỗi (status khác ok, mã HTTP ngoài 200–399) được service tự động forward sang hệ thống thứ 3 qua POST /external/domain-check — xem API_3nd.md.",

  "pager.range": "{from}–{to}",
  "pager.empty": "Không có bản ghi",
  "pager.prev": "Trang trước",
  "pager.next": "Trang sau",

  "appcfg.title": "Phiên bản app",
  "appcfg.subtitle": "Cấu hình bản phát hành để app mobile tự kiểm tra cập nhật",
  "appcfg.section": "Cấu hình phát hành",
  "appcfg.versionCode": "Version code",
  "appcfg.versionCodeHint": "Số nguyên tăng dần của bản mới nhất (vd 8)",
  "appcfg.versionName": "Tên phiên bản",
  "appcfg.versionNameHint": "Ví dụ 1.5.0",
  "appcfg.minVersionCode": "Version code tối thiểu",
  "appcfg.minVersionCodeHint": "Máy dưới mốc này sẽ bị buộc cập nhật",
  "appcfg.linkAPK": "Link tải APK",
  "appcfg.linkAPKHint": "URL tải bản cài mới nhất",
  "appcfg.uploadApk": "Upload file APK",
  "appcfg.uploadApkHint": "Upload trực tiếp lên server thay vì tự host — tối đa 500MB, chỉ nhận .apk",
  "appcfg.uploadApkAction": "Chọn file APK…",
  "appcfg.uploadApkCurrent": "Tải file APK đang lưu",
  "appcfg.uploadApkSaved": "Đã upload APK — link tải đã được cập nhật.",
  "appcfg.uploadApkDropHint": "hoặc kéo thả file .apk vào đây",
  "appcfg.uploadApkInvalid": "Chỉ chấp nhận file .apk",
  "appcfg.releaseNotes": "Ghi chú phát hành",
  "appcfg.releaseNotesHint": "Nội dung hiển thị cho người dùng khi có bản mới",
  "appcfg.updateEnabled": "Bật báo cập nhật",
  "appcfg.updateEnabledHint": "Tắt thì app luôn coi như không có bản mới",
  "appcfg.autoCheckEnabled": "Bật lịch tự động check",
  "appcfg.autoCheckEnabledHint": "Tắt thì device không tự động chạy check theo lịch",
  "appcfg.checkIntervalMinutes": "Chu kỳ check (phút)",
  "appcfg.checkIntervalMinutesHint": "Khoảng cách giữa các lượt check tự động (1–1440)",
  "appcfg.checkRounds": "Số round mỗi lượt",
  "appcfg.checkRoundsHint": "Số lần check trong 1 lượt (1–20)",
  "appcfg.roundIntervalSeconds": "Khoảng cách round (giây)",
  "appcfg.roundIntervalSecondsHint": "Thời gian nghỉ giữa các round (0–3600)",
  "appcfg.save": "Lưu cấu hình",
  "appcfg.saved": "Đã lưu cấu hình phiên bản.",
  "appcfg.updatedAt": "Cập nhật lần cuối",
  "appcfg.previewTitle": "App đang cài sẽ thấy",
  "appcfg.previewOff": "Đang tắt báo cập nhật — app sẽ không thấy bản mới.",
  "appcfg.previewOn": "Máy cũ hơn {min} (version code) sẽ bị buộc cập nhật lên {name}.",
  "appcfg.webhookSection": "Bảo trì Webhook bên thứ 3",
  "appcfg.webhookSectionHint": "Cấu hình chặn bên thứ 3 (như hệ thống logging ngoài) gọi webhook POST /external/domain-check khi cần bảo trì hệ thống.",
  "appcfg.webhookMaintenanceEnabled": "Bật chế độ bảo trì Webhook",
  "appcfg.webhookMaintenanceEnabledHint": "Khi bật, mọi yêu cầu webhook từ bên thứ 3 sẽ bị từ chối với HTTP 503 Service Unavailable",
  "appcfg.webhookMaintenanceActive": "ĐANG BẬT BẢO TRÌ — Bên thứ 3 không thể gọi webhook vào hệ thống.",
  "appcfg.webhookMaintenanceInactive": "Webhook đang hoạt động bình thường — Tiếp nhận dữ liệu từ bên thứ 3.",
  "appcfg.webhookMaintenanceMessage": "Thông điệp phản hồi bảo trì",
  "appcfg.webhookMaintenanceMessageHint": "Thông báo trả về cho bên thứ 3 trong response body (để trống sẽ dùng thông báo mặc định)",
  "appcfg.webhookMaintenanceMessagePlaceholder": "Hệ thống đang bảo trì webhook, tạm thời không nhận dữ liệu từ bên thứ 3",

  "roles.title": "Vai trò & Phân quyền",
  "roles.subtitle": "Quản lý vai trò và phân quyền động cho từng tính năng CMS",
  "roles.add": "Thêm vai trò",
  "roles.edit": "Sửa vai trò",
  "roles.formNew": "Thêm vai trò mới",
  "roles.formEdit": "Sửa vai trò \"{name}\"",
  "roles.name": "Mã vai trò",
  "roles.nameHint": "Tên định danh (vd: monitor, support_lead, chữ thường không dấu)",
  "roles.description": "Mô tả vai trò",
  "roles.descriptionHint": "Giải thích trách nhiệm của vai trò này",
  "roles.permissions": "Danh sách quyền",
  "roles.permissionsCount": "{count} quyền",
  "roles.isSystem": "Loại vai trò",
  "roles.systemRole": "Hệ thống",
  "roles.customRole": "Tuỳ chỉnh",
  "roles.confirmDelete": "Bạn có chắc muốn xoá vai trò \"{name}\" không?",
  "roles.cannotDeleteSystem": "Không thể xoá vai trò hệ thống.",
  "roles.created": "Ngày tạo",
  "roles.selectAll": "Chọn tất cả",
  "roles.unselectAll": "Bỏ chọn tất cả",
  "roles.saveSuccess": "Đã lưu vai trò thành công.",
  "roles.deleteSuccess": "Đã xoá vai trò thành công.",
  "roles.featureAll": "Toàn quyền (*)",

  "iprules.title": "Chặn IP CMS",
  "iprules.subtitle": "Chặn hoặc cho phép truy cập theo từng tính năng CMS. Mobile client app luôn được miễn trừ.",
  "iprules.myIp": "IP của bạn hiện tại",
  "iprules.isRoot": "Root IP (.env)",
  "iprules.rootBadge": "👑 Root IP (.env)",
  "iprules.rootHint": "IP này được cấu hình trong CMS_ROOT_IPS của file .env máy chủ, có quyền miễn trừ tuyệt đối khỏi mọi luật chặn CMS.",
  "iprules.add": "Thêm luật IP",
  "iprules.edit": "Sửa luật IP",
  "iprules.formNew": "Thêm luật IP mới",
  "iprules.formEdit": "Sửa luật IP",
  "iprules.feature": "Tính năng áp dụng",
  "iprules.allFeatures": "Tất cả tính năng CMS (all)",
  "iprules.ruleType": "Hành động",
  "iprules.block": "Chặn (BLOCK)",
  "iprules.allow": "Cho phép (ALLOW)",
  "iprules.ip": "Địa chỉ IP hoặc CIDR",
  "iprules.ipHint": "Ví dụ: 192.168.1.100 hoặc 10.0.0.0/24",
  "iprules.useMyIp": "Lấy IP của tôi",
  "iprules.description": "Mô tả / Ghi chú",
  "iprules.descriptionHint": "Lý do chặn hoặc cho phép",
  "iprules.status": "Trạng thái",
  "iprules.active": "Đang hiệu lực",
  "iprules.inactive": "Tạm dừng",
  "iprules.toggleActive": "Bật / tắt hiệu lực luật IP này",
  "iprules.confirmDelete": "Bạn có chắc muốn xoá luật IP \"{ip}\" không?",
  "iprules.filterFeature": "Lọc theo tính năng",
  "iprules.filterType": "Lọc loại luật",
  "iprules.saveSuccess": "Đã lưu luật IP thành công.",
  "iprules.deleteSuccess": "Đã xoá luật IP thành công.",

  "adminlogs.title": "Nhật ký thao tác của Quản trị viên (Admin Logs)",
  "adminlogs.subtitle": "Ghi lại chi tiết mọi hành động, request body và địa chỉ IP thực hiện",
  "adminlogs.cleanBtn": "Dọn dẹp log cũ",
  "adminlogs.filterBtn": "Lọc dữ liệu",
  "adminlogs.time": "Thời gian",
  "adminlogs.user": "Tài khoản",
  "adminlogs.action": "Hành động",
  "adminlogs.methodPath": "Phương thức & Đường dẫn",
  "adminlogs.ip": "IP thực hiện",
  "adminlogs.status": "Trạng thái",
  "adminlogs.duration": "Thời gian xử lý",
  "adminlogs.viewDetail": "Chi tiết",
  "adminlogs.empty": "Chưa có bản ghi nhật ký nào",
  "adminlogs.detailTitle": "Chi tiết nhật ký thao tác",
  "adminlogs.payloadBody": "Payload Request Body",
  "adminlogs.params": "Query / Route Params",
  "adminlogs.userAgent": "User-Agent",
  "adminlogs.errorMessage": "Thông báo lỗi",
  "adminlogs.cleanConfirm": "Nhập số ngày muốn giữ lại (xoá các log cũ hơn số ngày này):",
  "adminlogs.cleanSuccess": "Đã dọn dẹp {n} bản ghi nhật ký cũ.",
  "adminlogs.searchPh": "Tìm theo user, IP, action...",
  "adminlogs.allActions": "Tất cả hành động",
  "adminlogs.allStatuses": "Tất cả trạng thái",
  "adminlogs.paginationInfo": "Tổng cộng: {total} bản ghi | Trang {page} / {totalPages}",
  "adminlogs.invalidDays": "Số ngày không hợp lệ",
  "adminlogs.description": "Mô tả",
  "adminlogs.request": "Request",
  "action.auth.login": "Đăng nhập hệ thống CMS",
  "action.auth.logout": "Đăng xuất khỏi CMS",
  "action.auth.register": "Đăng ký tài khoản người dùng",
  "action.brand-links.create": "Thêm link giám sát mới",
  "action.brand-links.update": "Cập nhật link giám sát",
  "action.brand-links.delete": "Xoá link giám sát",
  "action.brand-links.restore": "Khôi phục link giám sát",
  "action.brand-links.check": "Kiểm tra link giám sát đơn lẻ",
  "action.brand-links.whois": "Kiểm tra WHOIS hạn domain",
  "action.brand-links.import": "Nhập hàng loạt link (JSON/CSV)",
  "action.brand-links.import-file": "Nhập link từ file (Excel/CSV)",
  "action.brand-links.bulk-delete": "Xoá hàng loạt link giám sát",
  "action.brand-links.check-all": "Kích hoạt kiểm tra toàn bộ link ngay",
  "action.brand-links.whois-all": "Quét WHOIS hạn domain toàn bộ link",
  "action.brand-links.change-owner": "Đổi chủ sở hữu cho danh sách link",
  "action.reports.generate": "Sinh báo cáo quét link toàn diện",
  "action.devices.create": "Đăng ký thiết bị kiểm tra mới",
  "action.devices.update": "Cập nhật thông tin thiết bị kiểm tra",
  "action.devices.delete": "Xoá thiết bị kiểm tra",
  "action.devices.rotate-key": "Cấp lại API key thiết bị",
  "action.device-checks.clear": "Xoá toàn bộ lịch sử kiểm tra của thiết bị",
  "action.device-checks.send-alert": "Gửi cảnh báo lỗi thiết bị",
  "action.brands.create": "Tạo thương hiệu mới",
  "action.brands.update": "Đổi tên thương hiệu",
  "action.brands.delete": "Xoá thương hiệu",
  "action.users.create": "Tạo tài khoản quản trị mới",
  "action.users.update": "Cập nhật thông tin tài khoản người dùng",
  "action.users.delete": "Xoá tài khoản người dùng",
  "action.users.change-password": "Đổi mật khẩu người dùng",
  "action.channels.create": "Tạo kênh thông báo mới",
  "action.channels.update": "Cập nhật kênh thông báo",
  "action.channels.delete": "Xoá kênh thông báo",
  "action.channels.test": "Kiểm tra gửi tin thử nghiệm",
  "action.channels.clone": "Sao chép kênh sang quốc gia khác",
  "action.roles.create": "Tạo vai trò phân quyền động mới",
  "action.roles.update": "Cập nhật vai trò phân quyền",
  "action.roles.delete": "Xoá vai trò phân quyền",
  "action.ip-rules.create": "Thêm quy tắc chặn/cho phép IP CMS mới",
  "action.ip-rules.update": "Cập nhật quy tắc IP CMS",
  "action.ip-rules.delete": "Xoá quy tắc IP CMS",
  "action.security.auto-ban-ip": "Tự động chặn IP nghi vấn tấn công",
  "action.app-config.update": "Cập nhật cấu hình ứng dụng",
  "action.app-config.upload-apk": "Tải lên phiên bản APK mới cho app mobile",
  "action.admin-logs.clean": "Dọn dẹp nhật ký thao tác cũ",
  "action.notifications.read": "Đánh dấu thông báo đã đọc",
  "action.notifications.read-all": "Đánh dấu tất cả thông báo đã đọc",

  "error.sessionExpired": "Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.",
  "error.generic": "Lỗi {status}",
  "error.download": "Không tải được file ({status})",
  "error.badRequest": "Dữ liệu gửi lên không hợp lệ.",
  "error.invalidCredentials": "Sai tên đăng nhập hoặc mật khẩu.",
  "error.forbidden": "Bạn không có quyền thực hiện thao tác này.",
  "error.notFound": "Không tìm thấy dữ liệu.",
  "error.conflict": "Dữ liệu bị trùng.",
  "error.tooManyRequests": "Quá nhiều yêu cầu. Vui lòng thử lại sau.",
  "error.server": "Máy chủ gặp sự cố, vui lòng thử lại sau.",
  "error.network": "Không kết nối được tới máy chủ.",
  "error.brandEmpty": "Brand để trống",
  "error.domainInvalid": "Domain không hợp lệ",
  "error.linkNotFound": "Link không tồn tại",
  "error.notOwner": "Link thuộc về user khác — bạn chỉ sửa/xoá được link của mình",
} as const;

export type MessageKey = keyof typeof vi;

const en: Record<MessageKey, string> = {
  "app.name": "Auto Check",
  "app.cms": "CMS",
  "app.tagline": "Multi-device link monitoring",
  "app.booting": "Starting up…",

  "common.loading": "Loading…",
  "common.close": "Close",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.refresh": "Refresh",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.details": "Details",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.all": "All",
  "common.optional": "Optional",
  "common.language": "Language",
  "common.logout": "Sign out",
  "country.label": "Country",
  "country.switchHint": "Switch the country you are working in — users, brands and links are kept separate",
  "country.lockedHint": "Your account only works within this country",
  "country.allCountries": "All countries",
  "country.vn": "Vietnam",
  "country.br": "Brazil",


  "status.healthy": "Healthy",
  "status.unhealthy": "Down",
  "status.unknown": "Unknown",

  "time.justNow": "just now",
  "time.minutes": "{n} min ago",
  "time.hours": "{n} h ago",
  "time.days": "{n} d ago",

  "nav.dashboard": "Overview",
  "nav.links": "Links",
  "nav.checks": "Check results",
  "nav.devices": "Devices",
  "nav.reports": "Reports",
  "nav.guide": "User Guide",
  "nav.channels": "Notify channels",
  "nav.users": "Users",
  "nav.brands": "Brands",
  "nav.appconfig": "App version",
  "nav.roles": "Roles & Permissions",
  "nav.iprules": "CMS IP Rules",
  "nav.logs": "Domain check logs",
  "nav.adminlogs": "Admin Logs",
  "nav.openapi": "OpenAPI Connect",

  "guide.title": "User Guide",
  "guide.subtitle": "Comprehensive documentation for CMS usage and device setup",
  "guide.search": "Search user guide...",
  "guide.toc": "Table of Contents",
  "guide.noResults": "No content found matching your search keyword.",
  "openapi.title": "OpenAPI Connect",
  "openapi.subtitle": "OpenAPI 3.0 specification portal, interactive Swagger UI, and integration code samples",
  "openapi.superadminBadge": "Superadmin Only",
  "openapi.openNewTab": "Open in new tab",
  "openapi.fullscreen": "Fullscreen",
  "openapi.reload": "Reload",
  "openapi.copied": "Copied!",
  "openapi.copyToken": "Copy Token",
  "openapi.specFiles": "OpenAPI Spec Files (YAML)",
  "openapi.authHeader": "API Authentication",
  "openapi.codeExamples": "Integration Code Examples",
  "openapi.postmanTitle": "Import into Postman (Postman Collection)",
  "openapi.postmanDesc": "Easily test & invoke APIs using Postman via direct URL import or pre-configured Collection JSON files",
  "openapi.postmanStep1": "Method 1 — Direct URL Import into Postman (Recommended)",
  "openapi.postmanStep1Desc": "Copy the OpenAPI or Collection link below, open Postman -> click Import (Ctrl/Cmd + O) -> paste the URL.",
  "openapi.postmanStep2": "Method 2 — Download Collection JSON",
  "openapi.postmanStep2Desc": "Download the JSON file below, open Postman -> click Import -> drag & drop the file.",
  "openapi.deniedTitle": "Access Denied",
  "openapi.deniedDesc": "OpenAPI integration hub is restricted to Superadmin accounts. Please contact the root administrator if you need API access.",
  "openapi.authDesc": "All CMS admin routes require a Superadmin Bearer JWT Token. Mobile devices use a Device Key.",
  "openapi.activeToken": "CURRENT ACTIVE SUPERADMIN TOKEN:",
  "openapi.sampleHeaders": "SAMPLE HEADERS:",
  "openapi.specDesc": "Download OpenAPI 3.0 YAML specifications for Postman, Insomnia, Swagger Editor, or auto-generating client SDKs.",
  "openapi.downloadYaml": "Download / Open YAML",
  "openapi.downloadJson": "Download JSON File",
  "openapi.copyUrl": "Copy URL",
  "openapi.copyPostmanUrl": "Copy Link to paste in Postman Import",
  "openapi.copySnippet": "Copy code snippet",
  "openapi.exitFullscreen": "Exit Fullscreen",
  "openapi.postmanGuideTitle": "POSTMAN IMPORT GUIDE:",
  "openapi.postmanStep1Title": "Open Postman & Click \"Import\"",
  "openapi.postmanStep1Text": "In the top-left corner of Postman, click Import (or press Ctrl + O / Cmd + O).",
  "openapi.postmanStep2Title": "Paste URL or Drag & Drop File",
  "openapi.postmanStep2Text": "You can paste the direct URL of the YAML / JSON Collection file or drag & drop the downloaded file. Postman supports both OpenAPI 3.0 YAML and Postman v2.1 JSON.",
  "openapi.postmanStep3Title": "Configure Token & Base URL",
  "openapi.postmanStep3Text": "In Postman, set the baseUrl variable and paste the Superadmin Bearer Token into the Collection's Authorization tab.",
  "guide.backToTop": "Back to top",
  "roles.descPlaceholder": "e.g. Monitor link status",
  "roles.allPermsHint": "Includes all current permissions and any new permissions added in the future",
  "iprules.commentPlaceholder": "e.g. Block spam IPs or allow office IP",

  "users.title": "Users",
  "users.subtitle": "Manage accounts & assign brands. Superadmin sees every brand; a brand user only sees their brand's links.",
  "users.add": "Add user",
  "users.username": "Username",
  "users.usernameHint": "At least 3 characters, unique",
  "users.password": "Password",
  "users.passwordHint": "At least 6 characters",
  "users.newPassword": "New password",
  "users.role": "Role",
  "users.roleHint": "superadmin: full access; admin/brand: locked to a brand",
  "users.role.superadmin": "Superadmin",
  "users.role.admin": "Admin",
  "users.role.brand": "Brand",
  "users.brand": "Brand",
  "users.brandHint": "The brand this account manages (matches the link's brand)",
  "users.brandNa": "Superadmin manages every brand — no assignment needed",
  "users.allBrands": "All brands",
  "users.noBrand": "No brand assigned",
  "users.locked": "Locked",
  "users.active": "Active",
  "users.lock": "Lock",
  "users.unlock": "Unlock",
  "users.lockAccount": "Lock this account (cannot sign in)",
  "users.created": "Created",
  "users.you": "you",
  "users.formNew": "Add user",
  "users.formEdit": "Edit user \"{name}\"",
  "users.create": "Create",
  "users.resetPw": "Reset password",
  "users.resetPwTitle": "Reset password — {name}",
  "users.resetPwOk": "Password changed for \"{name}\".",

  "brands.title": "Brands",
  "brands.subtitle": "Brand registry for assigning to users & links. Renaming a brand updates every link/user using it.",
  "brands.add": "Add brand",
  "brands.name": "Brand code",
  "brands.nameHint": "Letters, digits, . _ - (no spaces), e.g. demo, acme",
  "brands.note": "Note",
  "brands.noteHint": "Full name / description (optional)",
  "brands.choose": "— Select brand —",
  "brands.new": "New brand",
  "brands.add2": "Add",
  "brands.namePlaceholder": "New brand code…",
  "brands.links": "Links",
  "brands.users": "Users",
  "brands.created": "Created",
  "brands.formNew": "Add brand",
  "brands.formEdit": "Edit brand \"{name}\"",
  "brands.create": "Create",
  "brands.confirmDelete": "Delete brand \"{name}\"?",

  "chan.title": "Notification channels",
  "chan.subtitle": "{on}/{total} channels enabled · push alerts via Telegram, Lark",
  "chan.add": "Add channel",
  "chan.guide": "Setup guide",
  "chan.guideHint": "Not sure where to get the token/webhook? See the step-by-step guide.",
  "chan.formNew": "Add notification channel",
  "chan.formEdit": "Edit channel #{id}",
  "chan.name": "Channel name",
  "chan.nameHint": "A memorable name, e.g. \"Telegram - On-call\"",
  "chan.provider": "Provider",
  "chan.providerHint": "Both send to a GROUP",
  "chan.brand": "Brand",
  "chan.brandHint": "Only push links of this brand (default: all brands)",
  "chan.brandAll": "All brands",
  "chan.language": "Message language",
  "chan.languageHint": "The bot sends alerts to this group in the selected language (independent of the CMS language)",
  "chan.col.language": "Language",
  "chan.provider.telegram": "Telegram",
  "chan.provider.lark_webhook": "Lark (group webhook)",
  "chan.provider.lark": "Lark (group)",
  "chan.provider.lark_app": "Lark (app → user/chat)",
  "chan.targetType": "Target type",
  "chan.targetTypeHint": "For labeling only, does not change delivery",
  "chan.target.group": "Group",
  "chan.target.user": "User",
  "chan.target.channel": "Channel",
  "chan.target.none": "— None —",
  "chan.col.target": "Target",
  "chan.col.throttle": "Anti-spam",
  "chan.throttleHint": "The same link is re-sent only after this many minutes (0 = every time)",
  "chan.minutes": "{n} min",
  "chan.noThrottle": "Every time",
  "chan.notifyUnknown": "Also send \"suspected blocked\" state",
  "chan.notifyUnknownHint": "On: also send unknown links (WAF/bot block). Off: only clear failures (unhealthy).",
  "chan.enabled": "Enable this channel",
  "chan.enable": "Enable",
  "chan.disable": "Disable",
  "chan.status.on": "Enabled",
  "chan.status.off": "Disabled",
  "chan.save": "Save",
  "chan.create": "Create channel",
  "chan.country": "Country",
  "chan.countryHint": "Channel only receives alerts from links/devices of this country",
  "chan.countryFilter": "Country",
  "chan.countryAll": "All countries",
  "chan.test": "Send test",
  "chan.testError": "Test device error alert",
  "chan.testing": "Sending a test to \"{name}\"…",
  "chan.testingError": "Sending test device error alert to \"{name}\"…",
  "chan.testOk": "Test message sent to \"{name}\" successfully.",
  "chan.testErrorOk": "Test device error alert sent to \"{name}\" successfully.",
  "chan.clone": "Clone to another country",
  "chan.cloneTitle": "Clone channel to another country",
  "chan.cloneSubtitle": "Securely clones Bot Token / App Secret without re-entering secret keys.",
  "chan.cloneSource": "Source channel",
  "chan.cloneTargetCountry": "Target country",
  "chan.cloneName": "New channel name",
  "chan.cloneChatId": "New Chat ID (for this country)",
  "chan.cloneChatIdHint": "Telegram group ID (e.g. -100...) or Lark Chat ID (oc_...)",
  "chan.cloneThreadId": "Topic / Thread ID (optional)",
  "chan.cloneThreadIdHint": "If the target Telegram group has topics enabled",
  "chan.cloneTip": "Bot tokens and secrets are retained automatically — just provide the group Chat ID or Topic ID for the target country.",
  "chan.cloneBtn": "Clone & Create",
  "chan.cloneSuccess": "Channel cloned to {country} successfully.",
  "chan.testFail": "Test failed: {error}",
  "chan.delete": "Delete",
  "chan.confirmDelete": "Delete channel \"{name}\"?",
  "chan.lastSent": "Last sent",
  "chan.never": "Never",
  "chan.secretKeep": "Leave blank = keep the saved value",
  "chan.tg.botToken": "Bot token",
  "chan.tg.botTokenHint": "From @BotFather",
  "chan.tg.chatId": "Chat ID",
  "chan.tg.chatIdHint": "Group/user ID (e.g. -1001234567890). Add the bot to the group first.",
  "chan.tg.threadId": "Thread ID (optional)",
  "chan.tg.threadIdHint": "If the group has topics enabled",
  "chan.tg.sharedBotTip": "Tip: You can share 1 Bot Token across VN and Brazil by creating 2 channels (one for VN, one for BR) with separate Chat IDs or Topic/Thread IDs.",
  "chan.lw.webhookUrl": "Webhook URL",
  "chan.lw.webhookUrlHint": "Custom bot URL inside the Lark group",
  "chan.lw.secret": "Signing secret (optional)",
  "chan.lw.secretHint": "If the bot has signature verification on",
  "chan.lk.domain": "Server",
  "chan.lk.domainHint": "Lark international or Feishu (CN)",
  "chan.lk.chatId": "Group chat ID",
  "chan.lk.chatIdHint": "Group ID like oc_… (add the bot to the group first)",
  "chan.lk.appId": "App ID",
  "chan.lk.appIdHint": "cli_… from the Lark Developer Console",
  "chan.lk.appSecret": "App Secret",
  "chan.lk.chatIdLocked": "Enter App ID + App Secret first to pick a group",
  "chan.lk.chooseGroup": "— Select group —",
  "chan.lk.loading": "Loading groups…",
  "chan.lk.noChats": "The bot isn't in any group yet — add it to a group and retry",
  "chan.lk.mode": "Groups to alert",
  "chan.lk.modeBroadcast": "Every group the bot is in (recommended)",
  "chan.lk.modeChat": "One fixed group",
  "chan.lk.modeBroadcastHint":
    "Just add the bot to a group and it starts receiving — no chat_id needed. The app needs the im:chat:readonly scope.",
  "chan.lk.modeChatHint": "Send to exactly one group — pick it by name below",
  "chan.lk.allGroups": "All groups",
  "chan.lk.preview": "Preview recipients",
  "chan.lk.previewHint": "Groups the bot is in — refreshed on every send",
  "chan.lk.previewBtn": "Load groups",
  "chan.lk.previewCount": "{n} group(s) will be alerted",
  "chan.lk.testBroadcastConfirm":
    "Channel \"{name}\" is in all-groups mode — the test message goes to EVERY group the bot is in. Continue?",
  "chan.la.domain": "Server",
  "chan.la.domainHint": "Lark international or Feishu (CN)",
  "chan.la.appId": "App ID",
  "chan.la.appSecret": "App Secret",
  "chan.la.receiveIdType": "Receive ID type",
  "chan.la.receiveId": "Recipient user/chat ID",
  "chan.la.receiveIdHint": "Value matching the chosen ID type (open_id, chat_id…)",

  "login.username": "Username",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.welcome": "Welcome back",
  "login.subtitle": "Sign in to manage your link monitoring system",
  "login.hero": "Monitor link health — know instantly if a link is truly down or just regionally blocked",
  "login.f1t": "Multi-source monitoring",
  "login.f1d": "Checks from the server and many real mobile devices",
  "login.f2t": "Regional block detection",
  "login.f2d": "Tell a truly dead link from one merely blocked by network/region",
  "login.f3t": "Instant alerts",
  "login.f3d": "Push notifications to Telegram and Lark the moment a link fails",

  "table.brand": "Brand",
  "table.domain": "Domain",
  "table.status": "Status",
  "table.http": "HTTP",
  "table.response": "Response",
  "table.error": "Error",
  "table.deviceHint": "Most recently checked device",
  "table.checkerIp": "Checked from IP",
  "table.checkerIpHint": "The IP that performed the check — this is the IP being blocked when the error is 403/429",
  "table.checkedAt": "Checked",
  "table.note": "Note",

  "check.err.blocked": "Blocked (HTTP {status}) — most likely WAF/bot protection; cannot conclude whether the link is alive",
  "check.err.http": "HTTP {status}",
  "check.err.timeout": "Timed out after {detail}ms",
  "check.err.network": "Network error: {detail}",
  "check.err.unknown": "Unknown error",
  "table.expiry": "Expires",
  "table.owner": "Owner",
  "table.country": "Country",
  "table.source": "Source",
  "table.device": "Device",
  "table.region": "Region",
  "table.network": "Network",
  "table.ip": "IP",
  "table.time": "Time",

  "notif.title": "Notifications",
  "notif.empty": "No notifications yet.",
  "notif.markAllRead": "Mark all read",
  "notif.toastTitle": "Link check failed",

  "dash.title": "Overview",
  "dash.subtitle": "System-wide status as of the most recent check",
  "dash.checkAll": "Check all",
  "dash.generateReport": "Generate report",
  "dash.totalLinks": "Total links",
  "dash.activeDevices": "Active devices",
  "dash.attention": "Needs attention",
  "dash.viewAllLinks": "View all links →",
  "dash.allHealthy": "All links are healthy 🎉",
  "dash.latestReport": "Latest report",
  "dash.viewHistory": "History →",
  "dash.noReport": "No reports yet.",
  "dash.generatedAt": "Generated",
  "dash.duration": "Duration",
  "dash.ratio": "Total / Healthy / Down",

  "trigger.cron": "Scheduled",
  "trigger.manual": "Manual",

  "links.title": "Links",
  "links.subtitle": "{n} links monitored",
  "links.subtitleDisabled": "{n} links disabled",
  "links.exportCsv": "Export CSV",
  "links.exportJson": "Export JSON",
  "links.import": "Bulk import",
  "links.add": "+ Add link",
  "links.search": "Search by brand or domain…",
  "links.check": "Check",
  "links.compare": "Compare",
  "links.disable": "Disable",
  "links.confirmDelete": 'Disable link "{domain}" from brand {brand}? It is not deleted — check history is kept and it can be restored later from the "Disabled" tab.',
  "links.emptyFiltered": "No links match the current filter.",
  "links.empty": "No links yet — add your first one.",
  "links.formNew": "Add new link",
  "links.formEdit": "Edit link #{id}",
  "links.domainHint": "Full URL, e.g. https://example.com",
  "links.note": "Note",
  "links.noteHint": "Optional note about the link (purpose, campaign...)",
  "links.notePlaceholder": "e.g. Ads campaign link, partner link...",
  "links.expiryLabel": "Expiry date",
  "links.create": "Create link",
  "links.save": "Save changes",
  "links.onlyMine": "My links",
  "links.onlyMineHint": "Show only links I created",
  "links.ownerShared": "Brand-wide",
  "links.ownerSharedHint": "No owner set — anyone in the brand can edit it",
  "links.ownerMe": "you",
  "links.ownerHint": "Who is allowed to edit/delete this link",
  "links.ownerReadonlyHint": "Only a superadmin can change the owner",
  "links.notOwner": "Owned by another user",
  "links.bulkSelected": "{n} selected",
  "links.bulkClear": "Clear",
  "links.bulkOwner": "Change owner",
  "links.bulkOwnerTitle": "Change owner for {n} links",
  "links.bulkOwnerHint": "The new owner must be in the same brand — links from other brands are skipped",
  "links.bulkApply": "Update",
  "links.bulkDone": "Owner changed for {updated}/{requested} links",
  "links.bulkErrorRow": "Link #{id}: {error}",
  "links.bulkDelete": "Disable selected",
  "links.bulkDeleteTitle": "Disable {n} links",
  "links.bulkDeleteConfirm": "Disable the {n} selected links? They are not deleted — check history is kept and they can be restored later. You can only disable your own links unless you are an admin.",
  "links.bulkDeleteApply": "Disable",
  "links.bulkDeleteDone": "Deleted {disabled}/{requested} links",
  "links.viewActive": "Active",
  "links.viewDisabled": "Disabled",
  "links.restore": "Restore",
  "links.confirmRestore": 'Restore link "{domain}" from brand {brand}? It will be checked normally again.',
  "links.disabledHint": "These links are disabled — hidden from the check list, not sent to devices, and cannot be checked manually until restored.",
  "links.auditHistory": "Audit History",
  "links.viewList": "List view",
  "links.viewMatrix": "Matrix view",
  "audit.modalTitle": "Link Audit History",
  "audit.uptimeRate": "Uptime Rate",
  "audit.avgLatency": "Avg Latency",
  "audit.totalChecks": "Check Count",
  "audit.currentConsensus": "Current Status by Source",
  "audit.tabMatrix": "History Matrix",
  "audit.tabList": "Detailed List",
  "audit.tabAdminLogs": "Admin Logs",
  "audit.filterSource": "Source",
  "audit.limit": "records",
  "audit.roundTime": "Check Time",
  "audit.consensus": "Consensus",
  "audit.clusterNote": "Each row represents a concurrent check round across sources (clustered within 90s). Click any cell to view details.",
  "audit.noData": "No check records found for this link.",
  "audit.noAdminLogs": "No admin action logs found for this link.",
  "audit.detailTitle": "Check Result Detail",

  "import.title": "Bulk import links",
  "import.tabFile": "Upload file",
  "import.tabCsv": "Paste list",
  "import.submit": "Import",
  "import.fileLabel": "Data file",
  "import.fileHint": "Supports .xlsx, .xls, .csv, .json — a plain domain list, or columns brand, domain, note",
  "import.csvLabel": "Domain list",
  "import.csvHint": "One domain per line, or several separated by commas/spaces. To give each row its own brand/note, write it as brand,domain,note or domain,note.",
  "import.noFile": "No file selected.",
  "import.noDomains": "No domains pasted.",
  "import.brandHint": "Brand applied to every row that has none of its own — pick it here when pasting bare URLs",
  "import.brandFromData": "Use the brand column in the data",
  "import.noteLabel": "Default note",
  "import.noteHint": "Note applied to imported links if not present in the data",
  "import.notePlaceholder": "e.g. Batch 1, backup link...",
  "import.modeLabel": "Duplicate handling (brand + domain)",
  "import.modeUpsert": "Upsert — update duplicates",
  "import.modeSkip": "Skip — ignore duplicates",
  "import.received": "Received",
  "import.created": "Created",
  "import.updated": "Updated",
  "import.skipped": "Skipped",
  "import.failed": "Failed",
  "import.errorRow": "Row {row}: {error}",

  "devices.title": "Devices",
  "devices.subtitle": "{active}/{total} devices active",
  "devices.add": "+ Register device",
  "devices.empty": "No devices yet. Register one to get an API key for the mobile app.",
  "devices.apiKey": "API key",
  "devices.platform": "Platform",
  "devices.carrier": "Carrier",
  "devices.appVersion": "App version",
  "devices.model": "Model",
  "devices.lastSeen": "Last seen",
  "devices.locked": "Locked",
  "devices.lock": "Lock",
  "devices.unlock": "Unlock",
  "devices.history": "History",
  "devices.rotate": "Rotate key",
  "devices.confirmRotate": 'Rotate the API key for "{name}"? The old key is revoked immediately.',
  "devices.confirmDelete": 'Delete device "{name}"? This cannot be undone.',
  "devices.formNew": "Register new device",
  "devices.formEdit": "Edit device #{id}",
  "devices.nameLabel": "Device name",
  "devices.nameHint": "e.g. Pixel-HN-01",
  "devices.networkHint": "wifi / 4g / 5g",
  "devices.regionHint": "e.g. Hanoi",
  "devices.register": "Register",
  "devices.save": "Save changes",
  "devices.keyTitle": "Device API key",
  "devices.keyWarn":
    "⚠️ This key is shown only once. Copy it into the app now — once you close this dialog it cannot be retrieved.",
  "devices.keyDevice": "Device:",
  "devices.keyCopy": "Copy",
  "devices.keyCopied": "Copied ✓",
  "devices.keyAck": "I saved the key",
  "devices.keyHint": "The app sends this key via the X-Device-Key header when calling /device-api/*.",

  "devices.state.online": "Online",
  "devices.state.offline": "Offline",
  "devices.state.disabled": "Disabled",
  "devices.onlineHint": "Seen within the last {n} minutes",
  "devices.results": "Results",
  "devices.resultsTitle": "Check history — {name}",
  "devices.resultsEmpty": "This device hasn't reported any results yet.",
  "devices.quickAdd": "+ Device",

  "matrix.title": "Link × source matrix",
  "matrix.subtitle": "Latest result per source, with a consensus verdict",
  "matrix.link": "Link",
  "matrix.verdict": "Verdict",
  "matrix.onlyProblems": "Only show links with problems",
  "matrix.empty": "No matrix data yet.",
  "matrix.emptyFiltered": "No links currently have problems 🎉",
  "matrix.noColumns": "No check sources yet. Register a device to start comparing.",
  "matrix.staleNote": "Dimmed cell = older than {n} min · a · means that source never reported",
  "matrix.noResultsHint":
    "No source has reported on {n} links yet — that's why everything shows “Unknown”. Check that the device app is running and calling POST /device-api/results.",
  "matrix.staleLabel": "Stale after (minutes)",
  "matrix.failingRegions": "Failing regions",
  "matrix.failingNetworks": "Failing networks",
  "matrix.avgResponse": "Avg response",
  "matrix.missing": "No result yet",
  "matrix.stale": "Stale result",
  "matrix.serverColumn": "Server",

  "verdict.healthy": "Healthy",
  "verdict.partial": "Locally blocked",
  "verdict.down": "Really down",
  "verdict.unknown": "Unknown",
  "verdict.partialHint": "Only some sources fail — likely blocked on certain networks/regions",
  "verdict.downHint": "Every source fails — the link is genuinely down",

  "reports.title": "Reports",
  "reports.subtitle": "Snapshots generated manually with the button on the right",
  "reports.generate": "Generate now",
  "reports.empty": "No reports yet.",
  "reports.total": "Total",
  "reports.ratio": "Ratio",
  "reports.duration": "Duration",
  "reports.detailTitle": "Report #{id} — {date}",
  "reports.onlyFailed": "Only show links with problems",
  "reports.noDetails": "No links in this section.",

  "checks.title": "Check results",
  "checks.subtitle":
    "Compare the same link across devices, regions and networks — alongside the server's view",
  "checks.tabCompare": "Latest comparison",
  "checks.tabHistory": "Full history",
  "checks.link": "Link",
  "checks.allLinks": "All links",
  "checks.allDevices": "All devices",
  "checks.limit": "Rows",
  "checks.regionPlaceholder": "e.g. Hanoi",
  "checks.emptyFiltered": "No results match the current filter.",
  "checks.emptyCompare": "No device results yet. Register a device and let the app report back.",
  "checks.conflictWarn":
    "⚠️ {n} links disagree across agents — likely blocked on specific networks or regions.",
  "checks.conflict": "Disagreement",
  "checks.sourceServer": "Server",
  "checks.sourceDevice": "Device",
  "checks.clear": "Clear history",
  "checks.clearTitle": "Clear all check history",
  "checks.clearWarn":
    "This permanently deletes ALL check history for EVERY brand (both server and device results). It cannot be undone.",
  "checks.clearKeeps":
    "Each link's latest status, reports and notifications are kept — only the detailed history table is wiped. Afterwards this page and the matrix stay empty until the next check.",
  "checks.clearPromptLabel": "Type “{word}” to confirm",
  "checks.clearConfirmAction": "Delete permanently",
  "checks.clearDone": "Deleted {n} history rows.",
  "checks.sendAlert": "Send error alert to Bot",
  "checks.sendAlertTitle": "Send device error alert to Bot",
  "checks.sendAlertSubtitle": "Scan recent errors of check devices in the region and dispatch to Telegram/Lark bot.",
  "checks.sendAlertBtn": "Send alert now",
  "checks.sendAlertHours": "Timeframe",
  "checks.sendAlertCountry": "Region / Country",
  "checks.sendAlertDevice": "Device",
  "checks.sendAlertSuccess": "Sent device error alert successfully.",

  "logs.title": "Domain check logs",
  "logs.subtitle":
    "Logs from the Domain Check Logger API — a separate service from the main link-check system. Includes error events forwarded to a third-party system.",
  "logs.notConfigured":
    "Domain Check Logger API is not configured (missing BUN_PUBLIC_LOGS_API_BASE / _KEY / _SECRET at build time).",
  "logs.location": "Location",
  "logs.result": "Result",
  "logs.resultAll": "All results",
  "logs.resultSuccess": "Success",
  "logs.resultError": "Error",
  "logs.brandHint": "Only applies with a master key — a regular key is always locked to one brand",
  "logs.cmsName": "Source CMS",
  "logs.receivedAt": "Logged at",
  "logs.checkedAt": "Client checked at",
  "logs.geo": "Location (geo)",
  "logs.geoNone": "No geo data",
  "logs.empty": "No logs match the filter.",
  "logs.totalCount": "{n} entries",
  "logs.thirdParty":
    "Error logs (status other than ok, HTTP code outside 200–399) are auto-forwarded to a third-party system via POST /external/domain-check — see API_3nd.md.",

  "pager.range": "{from}–{to}",
  "pager.empty": "No records",
  "pager.prev": "Previous page",
  "pager.next": "Next page",

  "appcfg.title": "App version",
  "appcfg.subtitle": "Release settings the mobile app uses to check for updates",
  "appcfg.section": "Release configuration",
  "appcfg.versionCode": "Version code",
  "appcfg.versionCodeHint": "Monotonic integer of the latest build (e.g. 8)",
  "appcfg.versionName": "Version name",
  "appcfg.versionNameHint": "e.g. 1.5.0",
  "appcfg.minVersionCode": "Minimum version code",
  "appcfg.minVersionCodeHint": "Devices below this are forced to update",
  "appcfg.linkAPK": "APK download link",
  "appcfg.linkAPKHint": "URL to download the latest build",
  "appcfg.uploadApk": "Upload APK file",
  "appcfg.uploadApkHint": "Upload directly to the server instead of self-hosting — max 500MB, .apk only",
  "appcfg.uploadApkAction": "Choose APK file…",
  "appcfg.uploadApkCurrent": "Download stored APK",
  "appcfg.uploadApkSaved": "APK uploaded — the download link was updated.",
  "appcfg.uploadApkDropHint": "or drag & drop an .apk file here",
  "appcfg.uploadApkInvalid": "Only .apk files are accepted",
  "appcfg.releaseNotes": "Release notes",
  "appcfg.releaseNotesHint": "Shown to users when a new version is available",
  "appcfg.updateEnabled": "Enable update prompts",
  "appcfg.updateEnabledHint": "When off, the app always sees no update",
  "appcfg.autoCheckEnabled": "Enable auto-check schedule",
  "appcfg.autoCheckEnabledHint": "When off, devices won't run scheduled auto checks",
  "appcfg.checkIntervalMinutes": "Check interval (minutes)",
  "appcfg.checkIntervalMinutesHint": "Time between auto-check runs (1–1440)",
  "appcfg.checkRounds": "Rounds per run",
  "appcfg.checkRoundsHint": "Number of checks per run (1–20)",
  "appcfg.roundIntervalSeconds": "Round interval (seconds)",
  "appcfg.roundIntervalSecondsHint": "Pause between rounds (0–3600)",
  "appcfg.save": "Save configuration",
  "appcfg.saved": "Version configuration saved.",
  "appcfg.updatedAt": "Last updated",
  "appcfg.previewTitle": "What installed apps will see",
  "appcfg.previewOff": "Update prompts are off — apps will see no new version.",
  "appcfg.previewOn": "Devices older than {min} (version code) are forced to update to {name}.",
  "appcfg.webhookSection": "Third-Party Webhook Maintenance",
  "appcfg.webhookSectionHint": "Block third parties (such as external logging systems) from calling POST /external/domain-check webhook during system maintenance.",
  "appcfg.webhookMaintenanceEnabled": "Enable Webhook Maintenance Mode",
  "appcfg.webhookMaintenanceEnabledHint": "When enabled, all third-party webhook requests are rejected with HTTP 503 Service Unavailable",
  "appcfg.webhookMaintenanceActive": "MAINTENANCE ACTIVE — Third parties cannot send webhook requests.",
  "appcfg.webhookMaintenanceInactive": "Webhook is running normally — Accepting third-party requests.",
  "appcfg.webhookMaintenanceMessage": "Maintenance Response Message",
  "appcfg.webhookMaintenanceMessageHint": "Message returned to third parties in the response body (leave empty to use default message)",
  "appcfg.webhookMaintenanceMessagePlaceholder": "System webhook is under maintenance, temporarily not receiving 3rd-party data",

  "roles.title": "Roles & Permissions",
  "roles.subtitle": "Manage roles and dynamic permissions per CMS feature",
  "roles.add": "Add role",
  "roles.edit": "Edit role",
  "roles.formNew": "New role",
  "roles.formEdit": "Edit role \"{name}\"",
  "roles.name": "Role identifier",
  "roles.nameHint": "Unique identifier (e.g. monitor, support_lead, lowercase)",
  "roles.description": "Role description",
  "roles.descriptionHint": "Explain the responsibilities of this role",
  "roles.permissions": "Permissions",
  "roles.permissionsCount": "{count} permissions",
  "roles.isSystem": "Role type",
  "roles.systemRole": "System",
  "roles.customRole": "Custom",
  "roles.confirmDelete": "Are you sure you want to delete role \"{name}\"?",
  "roles.cannotDeleteSystem": "System roles cannot be deleted.",
  "roles.created": "Created at",
  "roles.selectAll": "Select all",
  "roles.unselectAll": "Deselect all",
  "roles.saveSuccess": "Role saved successfully.",
  "roles.deleteSuccess": "Role deleted successfully.",
  "roles.featureAll": "Full access (*)",

  "iprules.title": "CMS IP Rules",
  "iprules.subtitle": "Block or allow access per CMS feature. Mobile client apps are always exempted.",
  "iprules.myIp": "Your current IP",
  "iprules.isRoot": "Root IP (.env)",
  "iprules.rootBadge": "👑 Root IP (.env)",
  "iprules.rootHint": "This IP is configured in CMS_ROOT_IPS in the server .env, with absolute immunity from all CMS IP bans.",
  "iprules.add": "Add IP rule",
  "iprules.edit": "Edit IP rule",
  "iprules.formNew": "New IP rule",
  "iprules.formEdit": "Edit IP rule",
  "iprules.feature": "Target feature",
  "iprules.allFeatures": "All CMS features (all)",
  "iprules.ruleType": "Rule type",
  "iprules.block": "Block (BLOCK)",
  "iprules.allow": "Allow (ALLOW)",
  "iprules.ip": "IP address or CIDR",
  "iprules.ipHint": "Example: 192.168.1.100 or 10.0.0.0/24",
  "iprules.useMyIp": "Use my IP",
  "iprules.description": "Description / Note",
  "iprules.descriptionHint": "Reason for blocking or allowing",
  "iprules.status": "Status",
  "iprules.active": "Active",
  "iprules.inactive": "Inactive",
  "iprules.toggleActive": "Enable / disable this IP rule",
  "iprules.confirmDelete": "Are you sure you want to delete IP rule \"{ip}\"?",
  "iprules.filterFeature": "Filter by feature",
  "iprules.filterType": "Filter rule type",
  "iprules.saveSuccess": "IP rule saved successfully.",
  "iprules.deleteSuccess": "IP rule deleted successfully.",

  "adminlogs.title": "Administrator Audit Logs",
  "adminlogs.subtitle": "Detailed record of all admin actions, request bodies, and executing IP addresses",
  "adminlogs.cleanBtn": "Clean old logs",
  "adminlogs.filterBtn": "Filter",
  "adminlogs.time": "Time",
  "adminlogs.user": "User",
  "adminlogs.action": "Action",
  "adminlogs.methodPath": "Method & Path",
  "adminlogs.ip": "Executing IP",
  "adminlogs.status": "Status",
  "adminlogs.duration": "Duration",
  "adminlogs.viewDetail": "Detail",
  "adminlogs.empty": "No audit logs found",
  "adminlogs.detailTitle": "Audit Log Detail",
  "adminlogs.payloadBody": "Payload Request Body",
  "adminlogs.params": "Query / Route Params",
  "adminlogs.userAgent": "User-Agent",
  "adminlogs.errorMessage": "Error message",
  "adminlogs.cleanConfirm": "Enter retention days (delete logs older than):",
  "adminlogs.cleanSuccess": "Cleaned {n} old audit log records.",
  "adminlogs.searchPh": "Search user, IP, action...",
  "adminlogs.allActions": "All actions",
  "adminlogs.allStatuses": "All statuses",
  "adminlogs.paginationInfo": "Total: {total} records | Page {page} of {totalPages}",
  "adminlogs.invalidDays": "Invalid number of days",
  "adminlogs.description": "Description",
  "adminlogs.request": "Request",
  "action.auth.login": "Sign in to CMS",
  "action.auth.logout": "Sign out of CMS",
  "action.auth.register": "Register user account",
  "action.brand-links.create": "Add monitored link",
  "action.brand-links.update": "Update monitored link",
  "action.brand-links.delete": "Delete monitored link",
  "action.brand-links.restore": "Restore monitored link",
  "action.brand-links.check": "Check single link",
  "action.brand-links.whois": "Check domain WHOIS expiry",
  "action.brand-links.import": "Bulk import links (JSON/CSV)",
  "action.brand-links.import-file": "Import links from file (Excel/CSV)",
  "action.brand-links.bulk-delete": "Bulk delete links",
  "action.brand-links.check-all": "Check all links now",
  "action.brand-links.whois-all": "Scan all domains WHOIS expiry",
  "action.brand-links.change-owner": "Change owner for links",
  "action.reports.generate": "Generate scan report",
  "action.devices.create": "Register new device",
  "action.devices.update": "Update device info",
  "action.devices.delete": "Delete checking device",
  "action.devices.rotate-key": "Rotate device API key",
  "action.device-checks.clear": "Clear device check history",
  "action.device-checks.send-alert": "Send device alert",
  "action.brands.create": "Create new brand",
  "action.brands.update": "Update brand name",
  "action.brands.delete": "Delete brand",
  "action.users.create": "Create admin account",
  "action.users.update": "Update user account",
  "action.users.delete": "Delete user account",
  "action.users.change-password": "Change user password",
  "action.channels.create": "Create notification channel",
  "action.channels.update": "Update notification channel",
  "action.channels.delete": "Delete notification channel",
  "action.channels.test": "Send test notification",
  "action.channels.clone": "Clone channel to another country",
  "action.roles.create": "Create new role",
  "action.roles.update": "Update role permissions",
  "action.roles.delete": "Delete role",
  "action.ip-rules.create": "Add CMS IP rule",
  "action.ip-rules.update": "Update CMS IP rule",
  "action.ip-rules.delete": "Delete CMS IP rule",
  "action.security.auto-ban-ip": "Auto-ban suspicious IP",
  "action.app-config.update": "Update app configuration",
  "action.app-config.upload-apk": "Upload new APK version",
  "action.admin-logs.clean": "Clean old audit logs",
  "action.notifications.read": "Mark notification as read",
  "action.notifications.read-all": "Mark all notifications as read",

  "error.sessionExpired": "Your session has expired, please sign in again.",
  "error.generic": "Error {status}",
  "error.download": "Could not download the file ({status})",
  "error.badRequest": "The submitted data is invalid.",
  "error.invalidCredentials": "Wrong username or password.",
  "error.forbidden": "You don't have permission to do that.",
  "error.notFound": "Not found.",
  "error.conflict": "That record already exists.",
  "error.tooManyRequests": "Too many requests. Please try again later.",
  "error.server": "The server hit a problem, please try again later.",
  "error.network": "Could not reach the server.",
  "error.brandEmpty": "Brand is empty",
  "error.domainInvalid": "Invalid domain",
  "error.linkNotFound": "Link does not exist",
  "error.notOwner": "This link belongs to another user — you can only edit/delete your own",
};

const zh: Record<MessageKey, string> = {
  "app.name": "Auto Check",
  "app.cms": "CMS",
  "app.tagline": "多设备链接监控系统",
  "app.booting": "正在启动…",

  "common.loading": "加载中…",
  "common.close": "关闭",
  "common.cancel": "取消",
  "common.save": "保存",
  "common.refresh": "刷新",
  "common.edit": "编辑",
  "common.delete": "删除",
  "common.details": "详情",
  "common.copy": "复制",
  "common.copied": "已复制",
  "common.all": "全部",
  "common.optional": "选填",
  "common.language": "语言",
  "common.logout": "退出登录",
  "country.label": "国家",
  "country.switchHint": "切换当前操作的国家——用户、品牌和链接数据相互隔离",
  "country.lockedHint": "你的账号只能在该国家内操作",
  "country.allCountries": "所有国家",
  "country.vn": "越南",
  "country.br": "巴西",


  "status.healthy": "正常",
  "status.unhealthy": "故障",
  "status.unknown": "未知",

  "time.justNow": "刚刚",
  "time.minutes": "{n} 分钟前",
  "time.hours": "{n} 小时前",
  "time.days": "{n} 天前",

  "nav.dashboard": "总览",
  "nav.links": "链接管理",
  "nav.checks": "检测结果",
  "nav.devices": "设备",
  "nav.reports": "报告",
  "nav.guide": "用户指南",
  "nav.channels": "通知渠道",
  "nav.users": "用户",
  "nav.brands": "Brand",
  "nav.appconfig": "应用版本",
  "nav.roles": "角色与权限",
  "nav.iprules": "CMS IP 规则",
  "nav.logs": "域名检测日志",
  "nav.adminlogs": "管理员日志",
  "nav.openapi": "OpenAPI 开放接口",

  "guide.title": "用户指南",
  "guide.subtitle": "CMS 操作流程与设备接入详尽说明文档",
  "guide.search": "搜索指南内容...",
  "guide.toc": "目录",
  "guide.noResults": "未找到匹配的指南内容。",
  "openapi.title": "OpenAPI 开放接口",
  "openapi.subtitle": "OpenAPI 3.0 规范门户、交互式 Swagger UI 与开发集成代码示例",
  "openapi.superadminBadge": "仅限超级管理员",
  "openapi.openNewTab": "在新标签页打开",
  "openapi.fullscreen": "全屏切换",
  "openapi.reload": "重新加载",
  "openapi.copied": "已复制！",
  "openapi.copyToken": "复制 Token",
  "openapi.specFiles": "OpenAPI 规范文档 (YAML)",
  "openapi.authHeader": "接口认证",
  "openapi.codeExamples": "集成代码示例",
  "openapi.postmanTitle": "导入 Postman (Postman Collection)",
  "openapi.postmanDesc": "通过直链导入或预配置的 Collection JSON 文件，轻松使用 Postman 测试与调用 API",
  "openapi.postmanStep1": "方式 1 — 直接通过 URL 导入 Postman (推荐)",
  "openapi.postmanStep1Desc": "复制下方的 OpenAPI 或 Collection 链接，打开 Postman -> 点击 Import (Ctrl/Cmd + O) -> 粘贴链接即可。",
  "openapi.postmanStep2": "方式 2 — 下载 JSON Collection 文件",
  "openapi.postmanStep2Desc": "下载下方的 JSON 文件，打开 Postman -> 点击 Import -> 拖拽文件进入即可。",
  "openapi.deniedTitle": "访问被拒绝",
  "openapi.deniedDesc": "OpenAPI 开放接口功能仅限超级管理员使用。如需 API 集成，请联系最高管理员。",
  "openapi.authDesc": "所有 CMS 管理接口均需携带超级管理员 Bearer JWT Token。移动端设备使用 Device Key。",
  "openapi.activeToken": "当前超级管理员有效 TOKEN：",
  "openapi.sampleHeaders": "示例 HEADERS：",
  "openapi.specDesc": "下载 OpenAPI 3.0 YAML 规范文件，可导入 Postman、Insomnia、Swagger Editor 或自动生成客户端 SDK。",
  "openapi.downloadYaml": "下载 / 打开 YAML",
  "openapi.downloadJson": "下载 JSON 文件",
  "openapi.copyUrl": "复制 URL",
  "openapi.copyPostmanUrl": "复制导入链接",
  "openapi.copySnippet": "复制代码片段",
  "openapi.exitFullscreen": "退出全屏",
  "openapi.postmanGuideTitle": "POSTMAN 导入步骤：",
  "openapi.postmanStep1Title": "打开 Postman 并点击 \"Import\"",
  "openapi.postmanStep1Text": "在 Postman 左上角点击 Import（或快捷键 Ctrl + O / Cmd + O）。",
  "openapi.postmanStep2Title": "粘贴 URL 或拖拽文件",
  "openapi.postmanStep2Text": "可直接粘贴 YAML / JSON 集合文件的 URL，或拖拽已下载的文件进入。Postman 原生支持 OpenAPI 3.0 YAML 与 Postman v2.1 JSON。",
  "openapi.postmanStep3Title": "配置 Token 与 Base URL",
  "openapi.postmanStep3Text": "在 Postman 中设置 baseUrl 变量，并将上方的超级管理员 Bearer Token 粘贴至 Collection 的 Authorization 标签页中。",
  "guide.backToTop": "回到顶部",
  "roles.descPlaceholder": "例如：监控链接状态",
  "roles.allPermsHint": "包含所有现有权限以及未来新增的任何权限",
  "iprules.commentPlaceholder": "例如：拦截恶意 IP 或放行办公室 IP",

  "users.title": "用户",
  "users.subtitle": "管理账号并分配 brand。超级管理员可见所有 brand；brand 用户只能看到本 brand 的链接。",
  "users.add": "添加用户",
  "users.username": "用户名",
  "users.usernameHint": "至少 3 个字符，不可重复",
  "users.password": "密码",
  "users.passwordHint": "至少 6 个字符",
  "users.newPassword": "新密码",
  "users.role": "角色",
  "users.roleHint": "superadmin：全部权限；admin/brand：锁定到某个 brand",
  "users.role.superadmin": "超级管理员",
  "users.role.admin": "管理员",
  "users.role.brand": "Brand",
  "users.brand": "Brand",
  "users.brandHint": "该账号管理的 brand（与链接的 brand 对应）",
  "users.brandNa": "超级管理员管理所有 brand — 无需分配",
  "users.allBrands": "所有 brand",
  "users.noBrand": "未分配 brand",
  "users.locked": "已锁定",
  "users.active": "正常",
  "users.lock": "锁定",
  "users.unlock": "解锁",
  "users.lockAccount": "锁定此账号（无法登录）",
  "users.created": "创建时间",
  "users.you": "你",
  "users.formNew": "添加用户",
  "users.formEdit": "编辑用户“{name}”",
  "users.create": "创建",
  "users.resetPw": "重置密码",
  "users.resetPwTitle": "重置密码 — {name}",
  "users.resetPwOk": "已为“{name}”修改密码。",

  "brands.title": "Brand",
  "brands.subtitle": "用于分配给用户和链接的 brand 目录。重命名 brand 会同步更新所有使用它的链接/用户。",
  "brands.add": "添加 brand",
  "brands.name": "Brand 代码",
  "brands.nameHint": "字母、数字、. _ -（无空格），如 demo、acme",
  "brands.note": "备注",
  "brands.noteHint": "全称/描述（可选）",
  "brands.choose": "— 选择 brand —",
  "brands.new": "新 brand",
  "brands.add2": "添加",
  "brands.namePlaceholder": "新 brand 代码…",
  "brands.links": "链接数",
  "brands.users": "用户数",
  "brands.created": "创建时间",
  "brands.formNew": "添加 brand",
  "brands.formEdit": "编辑 brand“{name}”",
  "brands.create": "创建",
  "brands.confirmDelete": "删除 brand“{name}”？",

  "chan.title": "通知渠道",
  "chan.subtitle": "已启用 {on}/{total} 个渠道 · 通过 Telegram、Lark 推送告警",
  "chan.add": "添加渠道",
  "chan.guide": "设置指南",
  "chan.guideHint": "不知道在哪里获取 token/webhook？查看分步指南。",
  "chan.formNew": "添加通知渠道",
  "chan.formEdit": "编辑渠道 #{id}",
  "chan.name": "渠道名称",
  "chan.nameHint": "便于识别的名称，如“Telegram - 值班”",
  "chan.provider": "平台",
  "chan.providerHint": "两者都发送到群组",
  "chan.brand": "Brand",
  "chan.brandHint": "仅推送该 brand 的链接（默认：所有 brand）",
  "chan.brandAll": "所有 brand",
  "chan.language": "消息语言",
  "chan.languageHint": "机器人将使用所选语言向该群组发送告警（与 CMS 界面语言无关）",
  "chan.col.language": "语言",
  "chan.provider.telegram": "Telegram",
  "chan.provider.lark_webhook": "Lark（群 webhook）",
  "chan.provider.lark": "Lark（群）",
  "chan.provider.lark_app": "Lark（应用 → 用户/会话）",
  "chan.targetType": "目标类型",
  "chan.targetTypeHint": "仅用于标识，不影响发送方式",
  "chan.target.group": "群组",
  "chan.target.user": "用户",
  "chan.target.channel": "频道",
  "chan.target.none": "— 不设置 —",
  "chan.col.target": "目标",
  "chan.col.throttle": "防刷",
  "chan.throttleHint": "同一链接需间隔这么多分钟才再次发送（0 = 每次都发）",
  "chan.minutes": "{n} 分钟",
  "chan.noThrottle": "每次都发",
  "chan.notifyUnknown": "同时发送“疑似被拦截”状态",
  "chan.notifyUnknownHint": "开启：也发送 unknown 链接（WAF/机器人拦截）。关闭：仅发送明确失败（unhealthy）。",
  "chan.enabled": "启用此渠道",
  "chan.enable": "启用",
  "chan.disable": "停用",
  "chan.status.on": "已启用",
  "chan.status.off": "已停用",
  "chan.save": "保存",
  "chan.create": "创建渠道",
  "chan.country": "国家",
  "chan.countryHint": "渠道仅接收该国家的链接/设备报警",
  "chan.countryFilter": "国家",
  "chan.countryAll": "所有国家",
  "chan.test": "发送测试",
  "chan.testError": "测试设备报错",
  "chan.testing": "正在向“{name}”发送测试…",
  "chan.testingError": "正在向“{name}”发送设备报错测试…",
  "chan.testOk": "已成功向“{name}”发送测试消息。",
  "chan.testErrorOk": "已成功向“{name}”发送设备报错测试消息。",
  "chan.clone": "复制到其他国家",
  "chan.cloneTitle": "复制渠道到其他国家",
  "chan.cloneSubtitle": "安全复制当前渠道的 Bot Token / App Secret，无需重新获取或填写密钥。",
  "chan.cloneSource": "源渠道",
  "chan.cloneTargetCountry": "目标国家",
  "chan.cloneName": "新渠道名称",
  "chan.cloneChatId": "新 Chat ID（目标国家）",
  "chan.cloneChatIdHint": "Telegram 群 ID（如 -100...）或 Lark 群 Chat ID（oc_...）",
  "chan.cloneThreadId": "Topic / Thread ID（可选）",
  "chan.cloneThreadIdHint": "若目标 Telegram 群开启了话题（topics）",
  "chan.cloneTip": "系统会自动保留机器人的 Token/Secret — 仅需输入目标国家对应的群 Chat ID 或 Topic ID 即可。",
  "chan.cloneBtn": "复制并创建渠道",
  "chan.cloneSuccess": "已成功将渠道复制到 {country}。",
  "chan.testFail": "测试失败：{error}",
  "chan.delete": "删除",
  "chan.confirmDelete": "删除渠道“{name}”？",
  "chan.lastSent": "最近发送",
  "chan.never": "从未发送",
  "chan.secretKeep": "留空 = 保留已保存的值",
  "chan.tg.botToken": "Bot token",
  "chan.tg.botTokenHint": "从 @BotFather 获取",
  "chan.tg.chatId": "Chat ID",
  "chan.tg.chatIdHint": "群/用户 ID（如 -1001234567890）。请先把机器人加入群。",
  "chan.tg.threadId": "Thread ID（可选）",
  "chan.tg.threadIdHint": "若群开启了话题（topics）",
  "chan.tg.sharedBotTip": "提示：可通过创建 2 个渠道（一个选择越南，一个选择巴西）并配置不同的 Chat ID 或 Topic/Thread ID，在 VN 和 BR 之间复用同一个 Bot Token。",
  "chan.lw.webhookUrl": "Webhook URL",
  "chan.lw.webhookUrlHint": "Lark 群内自定义机器人地址",
  "chan.lw.secret": "签名密钥（可选）",
  "chan.lw.secretHint": "若机器人开启了签名校验",
  "chan.lk.domain": "服务器",
  "chan.lk.domainHint": "Lark 国际版或 Feishu（中国）",
  "chan.lk.chatId": "群 Chat ID",
  "chan.lk.chatIdHint": "群 ID，形如 oc_…（先把机器人加入群再获取）",
  "chan.lk.appId": "App ID",
  "chan.lk.appIdHint": "cli_…，来自 Lark 开发者后台",
  "chan.lk.appSecret": "App Secret",
  "chan.lk.chatIdLocked": "请先填写 App ID + App Secret 才能选择群",
  "chan.lk.chooseGroup": "— 选择群 —",
  "chan.lk.loading": "正在加载群列表…",
  "chan.lk.noChats": "机器人尚未加入任何群 — 先把它加入群再重试",
  "chan.lk.mode": "接收告警的群",
  "chan.lk.modeBroadcast": "机器人所在的全部群（推荐）",
  "chan.lk.modeChat": "固定一个群",
  "chan.lk.modeBroadcastHint":
    "只需把机器人加入群即可接收 — 无需填写 chat_id。应用需开通 im:chat:readonly 权限。",
  "chan.lk.modeChatHint": "只发送到一个群 — 在下方按名称选择",
  "chan.lk.allGroups": "全部群",
  "chan.lk.preview": "预览接收群",
  "chan.lk.previewBtn": "加载群列表",
  "chan.lk.previewHint": "机器人所在的群 — 每次发送时自动刷新",
  "chan.lk.previewCount": "将有 {n} 个群收到告警",
  "chan.lk.testBroadcastConfirm":
    "渠道“{name}”处于全部群模式 — 测试消息会发送到机器人所在的所有群。是否继续？",
  "chan.la.domain": "服务器",
  "chan.la.domainHint": "Lark 国际版或 Feishu（中国）",
  "chan.la.appId": "App ID",
  "chan.la.appSecret": "App Secret",
  "chan.la.receiveIdType": "接收 ID 类型",
  "chan.la.receiveId": "接收人/会话 ID",
  "chan.la.receiveIdHint": "与所选 ID 类型匹配的值（open_id、chat_id…）",

  "login.username": "用户名",
  "login.password": "密码",
  "login.submit": "登录",
  "login.welcome": "欢迎回来",
  "login.subtitle": "登录以管理链接监控系统",
  "login.hero": "监控链接健康 — 立即区分链接是真正失效还是仅被区域拦截",
  "login.f1t": "多源监控",
  "login.f1d": "来自服务器和多台真实移动设备的检测",
  "login.f2t": "区域拦截识别",
  "login.f2d": "区分真正失效的链接与仅被网络/区域拦截的链接",
  "login.f3t": "即时告警",
  "login.f3d": "链接出现问题时立即推送到 Telegram 和 Lark",

  "table.brand": "品牌",
  "table.domain": "域名",
  "table.status": "状态",
  "table.http": "HTTP",
  "table.response": "响应",
  "table.error": "错误",
  "table.deviceHint": "最近检测的设备",
  "table.checkerIp": "检测出口 IP",
  "table.checkerIpHint": "执行本次检测的 IP — 当错误为 403/429 时，被拦截的正是这个 IP",
  "table.checkedAt": "检测时间",
  "table.note": "备注",

  "check.err.blocked": "被拦截（HTTP {status}）— 很可能是 WAF/机器人防护，无法判定链接是否存活",
  "check.err.http": "HTTP {status}",
  "check.err.timeout": "超时（已等待 {detail}ms）",
  "check.err.network": "网络错误：{detail}",
  "check.err.unknown": "未知错误",
  "table.expiry": "到期",
  "table.owner": "所有者",
  "table.country": "国家",
  "table.source": "来源",
  "table.device": "设备",
  "table.region": "地区",
  "table.network": "网络",
  "table.ip": "IP",
  "table.time": "时间",

  "notif.title": "通知",
  "notif.empty": "暂无通知。",
  "notif.markAllRead": "全部标为已读",
  "notif.toastTitle": "链接检测失败",

  "dash.title": "总览",
  "dash.subtitle": "基于最近一次检测的系统整体状态",
  "dash.checkAll": "检测全部",
  "dash.generateReport": "生成报告",
  "dash.totalLinks": "链接总数",
  "dash.activeDevices": "在用设备",
  "dash.attention": "需要关注",
  "dash.viewAllLinks": "查看全部链接 →",
  "dash.allHealthy": "所有链接均正常 🎉",
  "dash.latestReport": "最新报告",
  "dash.viewHistory": "历史 →",
  "dash.noReport": "暂无报告。",
  "dash.generatedAt": "生成于",
  "dash.duration": "耗时",
  "dash.ratio": "总计 / 正常 / 故障",

  "trigger.cron": "定时",
  "trigger.manual": "手动",

  "links.title": "链接管理",
  "links.subtitle": "正在监控 {n} 条链接",
  "links.subtitleDisabled": "已停用 {n} 条链接",
  "links.exportCsv": "导出 CSV",
  "links.exportJson": "导出 JSON",
  "links.import": "批量导入",
  "links.add": "+ 添加链接",
  "links.search": "按品牌或域名搜索…",
  "links.check": "检测",
  "links.compare": "对比",
  "links.disable": "停用",
  "links.confirmDelete": "确定停用品牌 {brand} 的链接“{domain}”吗？不会被删除——检测历史仍会保留，之后可在“已停用”标签页恢复。",
  "links.emptyFiltered": "没有符合筛选条件的链接。",
  "links.empty": "还没有链接 — 先添加第一条吧。",
  "links.formNew": "添加新链接",
  "links.formEdit": "编辑链接 #{id}",
  "links.domainHint": "完整 URL，例如 https://example.com",
  "links.note": "备注",
  "links.noteHint": "链接附加备注（用途、投放计划等，可选）",
  "links.notePlaceholder": "例如：广告投放链接、合作伙伴链接...",
  "links.expiryLabel": "到期日期",
  "links.create": "创建链接",
  "links.save": "保存修改",
  "links.onlyMine": "我的链接",
  "links.onlyMineHint": "仅显示我创建的链接",
  "links.ownerShared": "品牌共用",
  "links.ownerSharedHint": "未指定所有者 — 该品牌下所有用户都可编辑",
  "links.ownerMe": "你",
  "links.ownerHint": "允许编辑/删除该链接的用户",
  "links.ownerReadonlyHint": "仅超级管理员可更改所有者",
  "links.notOwner": "属于其他用户",
  "links.bulkSelected": "已选择 {n} 条链接",
  "links.bulkClear": "取消选择",
  "links.bulkOwner": "变更所有者",
  "links.bulkOwnerTitle": "为 {n} 条链接变更所有者",
  "links.bulkOwnerHint": "新所有者须与链接同属一个品牌——其他品牌的链接会被跳过",
  "links.bulkApply": "更新",
  "links.bulkDone": "已变更 {updated}/{requested} 条链接的所有者",
  "links.bulkErrorRow": "链接 #{id}：{error}",
  "links.bulkDelete": "停用所选",
  "links.bulkDeleteTitle": "停用 {n} 条链接",
  "links.bulkDeleteConfirm": "确认停用所选的 {n} 条链接？不会被删除，检测历史仍会保留，之后可以恢复。除管理员外，只能停用自己的链接。",
  "links.bulkDeleteApply": "停用",
  "links.bulkDeleteDone": "已删除 {disabled}/{requested} 条链接",
  "links.viewActive": "生效中",
  "links.viewDisabled": "已停用",
  "links.restore": "恢复",
  "links.confirmRestore": "恢复品牌 {brand} 的链接“{domain}”？恢复后将重新正常检测。",
  "links.disabledHint": "这些链接已被停用 — 不会出现在检测列表中，设备也不会收到，恢复前无法手动检测。",
  "links.auditHistory": "审计历史",
  "links.viewList": "列表模式",
  "links.viewMatrix": "矩阵模式",
  "audit.modalTitle": "链接审计历史",
  "audit.uptimeRate": "可用率",
  "audit.avgLatency": "平均延迟",
  "audit.totalChecks": "检测次数",
  "audit.currentConsensus": "各检测源当前状态",
  "audit.tabMatrix": "历史矩阵",
  "audit.tabList": "详细列表",
  "audit.tabAdminLogs": "管理员日志",
  "audit.filterSource": "检测源",
  "audit.limit": "条记录",
  "audit.roundTime": "检测时间",
  "audit.consensus": "共识状态",
  "audit.clusterNote": "每行代表各源的一次并发检测轮次（90秒内聚合）。点击单元格查看详情。",
  "audit.noData": "此链接暂无检测记录。",
  "audit.noAdminLogs": "此链接暂无管理员操作记录。",
  "audit.detailTitle": "检测结果详情",

  "import.title": "批量导入链接",
  "import.tabFile": "上传文件",
  "import.tabCsv": "粘贴列表",
  "import.submit": "导入",
  "import.fileLabel": "数据文件",
  "import.fileHint": "支持 .xlsx、.xls、.csv、.json — 可只列域名，或 brand, domain, note 多列",
  "import.csvLabel": "域名列表",
  "import.csvHint": "每行一个域名，也可用逗号/空格分隔多个。若需指定品牌/备注，请写成 brand,domain,note 或 domain,note。",
  "import.noFile": "尚未选择文件。",
  "import.noDomains": "尚未粘贴任何域名。",
  "import.brandHint": "应用于所有未自带品牌的行 — 粘贴纯 URL 列表时在此选择",
  "import.brandFromData": "使用数据中的 brand 列",
  "import.noteLabel": "默认备注",
  "import.noteHint": "应用于导入链接的默认备注（若数据中未指定）",
  "import.notePlaceholder": "例如：第一批导入、备用链接...",
  "import.modeLabel": "重复处理（brand + domain）",
  "import.modeUpsert": "Upsert — 更新重复记录",
  "import.modeSkip": "Skip — 跳过重复记录",
  "import.received": "接收",
  "import.created": "新建",
  "import.updated": "更新",
  "import.skipped": "跳过",
  "import.failed": "失败",
  "import.errorRow": "第 {row} 行：{error}",

  "devices.title": "设备",
  "devices.subtitle": "{active}/{total} 台设备在用",
  "devices.add": "+ 注册设备",
  "devices.empty": "还没有设备。注册一台即可获取移动端 API key。",
  "devices.apiKey": "API key",
  "devices.platform": "平台",
  "devices.carrier": "运营商",
  "devices.appVersion": "应用版本",
  "devices.model": "型号",
  "devices.lastSeen": "最后在线",
  "devices.locked": "已锁定",
  "devices.lock": "锁定",
  "devices.unlock": "解锁",
  "devices.history": "历史",
  "devices.rotate": "重新签发",
  "devices.confirmRotate": "确定为“{name}”重新签发 API key 吗？旧 key 会立即失效。",
  "devices.confirmDelete": "确定删除设备“{name}”吗？此操作不可撤销。",
  "devices.formNew": "注册新设备",
  "devices.formEdit": "编辑设备 #{id}",
  "devices.nameLabel": "设备名称",
  "devices.nameHint": "例如 Pixel-HN-01",
  "devices.networkHint": "wifi / 4g / 5g",
  "devices.regionHint": "例如 河内",
  "devices.register": "注册",
  "devices.save": "保存修改",
  "devices.keyTitle": "设备 API key",
  "devices.keyWarn": "⚠️ 此 key 只显示一次。请立即复制到应用中 — 关闭本窗口后将无法再查看。",
  "devices.keyDevice": "设备：",
  "devices.keyCopy": "复制",
  "devices.keyCopied": "已复制 ✓",
  "devices.keyAck": "我已保存 key",
  "devices.keyHint": "应用调用 /device-api/* 时通过 X-Device-Key 请求头发送此 key。",

  "devices.state.online": "在线",
  "devices.state.offline": "离线",
  "devices.state.disabled": "已停用",
  "devices.onlineHint": "最近 {n} 分钟内有通信",
  "devices.results": "结果",
  "devices.resultsTitle": "检测历史 — {name}",
  "devices.resultsEmpty": "该设备尚未上报任何结果。",
  "devices.quickAdd": "+ 设备",

  "matrix.title": "链接 × 来源矩阵",
  "matrix.subtitle": "各来源的最新结果与共识判定",
  "matrix.link": "链接",
  "matrix.verdict": "判定",
  "matrix.onlyProblems": "只显示有问题的链接",
  "matrix.empty": "暂无矩阵数据。",
  "matrix.emptyFiltered": "当前没有出问题的链接 🎉",
  "matrix.noColumns": "还没有检测来源。注册设备后即可开始对比。",
  "matrix.staleNote": "灰色格 = 超过 {n} 分钟的旧结果 · 「·」表示该来源从未上报",
  "matrix.noResultsHint":
    "尚无任何来源为这 {n} 条链接上报结果 — 因此全部显示「未知」。请确认设备端应用已在运行并调用 POST /device-api/results。",
  "matrix.staleLabel": "多少分钟视为过期",
  "matrix.failingRegions": "故障地区",
  "matrix.failingNetworks": "故障网络",
  "matrix.avgResponse": "平均响应",
  "matrix.missing": "暂无结果",
  "matrix.stale": "结果已过期",
  "matrix.serverColumn": "服务器",

  "verdict.healthy": "正常",
  "verdict.partial": "局部屏蔽",
  "verdict.down": "确实宕机",
  "verdict.unknown": "未知",
  "verdict.partialHint": "只有部分来源失败 — 可能在特定网络/地区被屏蔽",
  "verdict.downHint": "所有来源均失败 — 链接确实宕机",

  "reports.title": "报告",
  "reports.subtitle": "通过右侧按钮手动生成快照",
  "reports.generate": "立即生成",
  "reports.empty": "暂无报告。",
  "reports.total": "总计",
  "reports.ratio": "比例",
  "reports.duration": "耗时",
  "reports.detailTitle": "报告 #{id} — {date}",
  "reports.onlyFailed": "只显示有问题的链接",
  "reports.noDetails": "此分类下没有链接。",

  "checks.title": "检测结果",
  "checks.subtitle": "跨设备、地区与网络对比同一条链接 — 并附上服务器视角",
  "checks.tabCompare": "最新对比",
  "checks.tabHistory": "完整历史",
  "checks.link": "链接",
  "checks.allLinks": "全部链接",
  "checks.allDevices": "全部设备",
  "checks.limit": "记录数",
  "checks.regionPlaceholder": "例如 河内",
  "checks.emptyFiltered": "没有符合筛选条件的结果。",
  "checks.emptyCompare": "尚无设备结果。注册设备并让应用上报即可。",
  "checks.conflictWarn": "⚠️ {n} 条链接在各代理间结果不一致 — 很可能在特定网络或地区被屏蔽。",
  "checks.conflict": "结果不一致",
  "checks.sourceServer": "服务器",
  "checks.sourceDevice": "设备",
  "checks.clear": "清空历史",
  "checks.clearTitle": "清空全部检测历史",
  "checks.clearWarn": "此操作将永久删除所有品牌的全部检测历史（含服务器与设备结果），且无法撤销。",
  "checks.clearKeeps":
    "每条链接的最新状态、报告与通知会保留 — 仅清除详细历史表。清空后，本页与矩阵将保持为空，直到下一次检测。",
  "checks.clearPromptLabel": "输入“{word}”以确认",
  "checks.clearConfirmAction": "永久删除",
  "checks.clearDone": "已删除 {n} 条历史记录。",
  "checks.sendAlert": "向Bot发送报错日志",
  "checks.sendAlertTitle": "向Bot发送设备报错日志",
  "checks.sendAlertSubtitle": "主动扫描该地区检测设备的最近报错并推送到Telegram/Lark Bot。",
  "checks.sendAlertBtn": "立即发送",
  "checks.sendAlertHours": "时间范围",
  "checks.sendAlertCountry": "地区 / 国家",
  "checks.sendAlertDevice": "设备",
  "checks.sendAlertSuccess": "已成功发送设备报错通知。",

  "logs.title": "域名检测日志",
  "logs.subtitle": "来自 Domain Check Logger API 的日志 —— 与主链接检测系统完全独立的服务，含转发到第三方系统的错误事件。",
  "logs.notConfigured": "尚未配置 Domain Check Logger API（构建时缺少 BUN_PUBLIC_LOGS_API_BASE / _KEY / _SECRET）。",
  "logs.location": "位置",
  "logs.result": "结果",
  "logs.resultAll": "全部结果",
  "logs.resultSuccess": "成功",
  "logs.resultError": "错误",
  "logs.brandHint": "仅对 master key 生效 —— 普通 key 始终锁定在单个 brand",
  "logs.cmsName": "来源 CMS",
  "logs.receivedAt": "记录时间",
  "logs.checkedAt": "客户端检测时间",
  "logs.geo": "位置（geo）",
  "logs.geoNone": "无 geo 数据",
  "logs.empty": "没有符合筛选条件的日志。",
  "logs.totalCount": "{n} 条记录",
  "logs.thirdParty": "错误日志（status 非 ok、HTTP 码不在 200–399）会自动通过 POST /external/domain-check 转发到第三方系统 —— 详见 API_3nd.md。",

  "pager.range": "{from}–{to}",
  "pager.empty": "暂无记录",
  "pager.prev": "上一页",
  "pager.next": "下一页",

  "appcfg.title": "应用版本",
  "appcfg.subtitle": "供移动端检查更新的发布配置",
  "appcfg.section": "发布配置",
  "appcfg.versionCode": "版本号 (version code)",
  "appcfg.versionCodeHint": "最新版本的递增整数（例如 8）",
  "appcfg.versionName": "版本名称",
  "appcfg.versionNameHint": "例如 1.5.0",
  "appcfg.minVersionCode": "最低版本号",
  "appcfg.minVersionCodeHint": "低于此值的设备将被强制更新",
  "appcfg.linkAPK": "APK 下载链接",
  "appcfg.linkAPKHint": "最新安装包的下载 URL",
  "appcfg.uploadApk": "上传 APK 文件",
  "appcfg.uploadApkHint": "直接上传到服务器，无需自行托管 — 最大 500MB，仅支持 .apk",
  "appcfg.uploadApkAction": "选择 APK 文件…",
  "appcfg.uploadApkCurrent": "下载已保存的 APK",
  "appcfg.uploadApkSaved": "APK 上传成功 — 下载链接已更新。",
  "appcfg.uploadApkDropHint": "或将 .apk 文件拖放到此处",
  "appcfg.uploadApkInvalid": "仅支持 .apk 文件",
  "appcfg.releaseNotes": "发布说明",
  "appcfg.releaseNotesHint": "有新版本时向用户显示的内容",
  "appcfg.updateEnabled": "启用更新提示",
  "appcfg.updateEnabledHint": "关闭后应用将始终视为无新版本",
  "appcfg.autoCheckEnabled": "启用自动检查计划",
  "appcfg.autoCheckEnabledHint": "关闭后设备将不会按计划自动检查",
  "appcfg.checkIntervalMinutes": "检查周期（分钟）",
  "appcfg.checkIntervalMinutesHint": "自动检查之间的间隔（1–1440）",
  "appcfg.checkRounds": "每轮检查次数",
  "appcfg.checkRoundsHint": "每次运行的检查次数（1–20）",
  "appcfg.roundIntervalSeconds": "轮次间隔（秒）",
  "appcfg.roundIntervalSecondsHint": "各轮之间的暂停时间（0–3600）",
  "appcfg.save": "保存配置",
  "appcfg.saved": "版本配置已保存。",
  "appcfg.updatedAt": "最后更新",
  "appcfg.previewTitle": "已安装应用将看到",
  "appcfg.previewOff": "更新提示已关闭 — 应用将看不到新版本。",
  "appcfg.previewOn": "版本号低于 {min} 的设备将被强制更新到 {name}。",
  "appcfg.webhookSection": "第三方 Webhook 维护模式",
  "appcfg.webhookSectionHint": "在系统维护期间，阻止第三方（如外部日志系统）调用 POST /external/domain-check Webhook 接口。",
  "appcfg.webhookMaintenanceEnabled": "开启 Webhook 维护模式",
  "appcfg.webhookMaintenanceEnabledHint": "开启后，所有第三方 Webhook 请求将被拒绝并返回 HTTP 503 Service Unavailable",
  "appcfg.webhookMaintenanceActive": "维护模式已开启 — 第三方无法向系统发送 Webhook 请求。",
  "appcfg.webhookMaintenanceInactive": "Webhook 正常运行中 — 正常接收第三方请求。",
  "appcfg.webhookMaintenanceMessage": "维护响应消息",
  "appcfg.webhookMaintenanceMessageHint": "在响应体中返回给第三方的提示信息（留空则使用默认提示）",
  "appcfg.webhookMaintenanceMessagePlaceholder": "系统 Webhook 正在维护中，暂时不接收第三方数据",

  "roles.title": "角色与权限",
  "roles.subtitle": "管理角色及各 CMS 功能的动态权限",
  "roles.add": "添加角色",
  "roles.edit": "编辑角色",
  "roles.formNew": "新建角色",
  "roles.formEdit": "编辑角色 \"{name}\"",
  "roles.name": "角色标识",
  "roles.nameHint": "标识名称（例如：monitor, support_lead，小写字母）",
  "roles.description": "角色描述",
  "roles.descriptionHint": "说明该角色的职责与用途",
  "roles.permissions": "权限列表",
  "roles.permissionsCount": "{count} 项权限",
  "roles.isSystem": "角色类型",
  "roles.systemRole": "系统内置",
  "roles.customRole": "自定义",
  "roles.confirmDelete": "确定要删除角色 \"{name}\" 吗？",
  "roles.cannotDeleteSystem": "无法删除系统内置角色。",
  "roles.created": "创建时间",
  "roles.selectAll": "全选",
  "roles.unselectAll": "取消全选",
  "roles.saveSuccess": "角色保存成功。",
  "roles.deleteSuccess": "角色删除成功。",
  "roles.featureAll": "全部权限 (*)",

  "iprules.title": "CMS IP 规则",
  "iprules.subtitle": "按 CMS 功能阻止或允许 IP 访问。移动端客户端永远不受限制。",
  "iprules.myIp": "您当前的 IP",
  "iprules.isRoot": "Root IP (.env)",
  "iprules.rootBadge": "👑 Root IP (.env)",
  "iprules.rootHint": "此 IP 在服务器 .env 的 CMS_ROOT_IPS 中配置，对所有 CMS 阻止规则拥有绝对豁免权。",
  "iprules.add": "添加 IP 规则",
  "iprules.edit": "编辑 IP 规则",
  "iprules.formNew": "新建 IP 规则",
  "iprules.formEdit": "编辑 IP 规则",
  "iprules.feature": "适用功能",
  "iprules.allFeatures": "所有 CMS 功能 (all)",
  "iprules.ruleType": "规则类型",
  "iprules.block": "阻止 (BLOCK)",
  "iprules.allow": "允许 (ALLOW)",
  "iprules.ip": "IP 地址或 CIDR",
  "iprules.ipHint": "例如：192.168.1.100 或 10.0.0.0/24",
  "iprules.useMyIp": "使用我的 IP",
  "iprules.description": "描述 / 备注",
  "iprules.descriptionHint": "阻止或允许的原因",
  "iprules.status": "状态",
  "iprules.active": "生效中",
  "iprules.inactive": "已停用",
  "iprules.toggleActive": "启用 / 停用该 IP 规则",
  "iprules.confirmDelete": "确定要删除 IP 规则 \"{ip}\" 吗？",
  "iprules.filterFeature": "按功能筛选",
  "iprules.filterType": "按类型筛选",
  "iprules.saveSuccess": "IP 规则保存成功。",
  "iprules.deleteSuccess": "IP 规则删除成功。",

  "adminlogs.title": "管理员操作审计日志",
  "adminlogs.subtitle": "记录所有管理员操作、请求体(Body)以及执行IP地址",
  "adminlogs.cleanBtn": "清理历史日志",
  "adminlogs.filterBtn": "筛选数据",
  "adminlogs.time": "时间",
  "adminlogs.user": "账号",
  "adminlogs.action": "操作",
  "adminlogs.methodPath": "方法与路径",
  "adminlogs.ip": "执行IP",
  "adminlogs.status": "状态",
  "adminlogs.duration": "耗时",
  "adminlogs.viewDetail": "详情",
  "adminlogs.empty": "暂无操作日志",
  "adminlogs.detailTitle": "操作日志详情",
  "adminlogs.payloadBody": "请求体 (Request Body)",
  "adminlogs.params": "查询 / 路由参数",
  "adminlogs.userAgent": "User-Agent",
  "adminlogs.errorMessage": "错误信息",
  "adminlogs.cleanConfirm": "请输入保留天数（清理早于此天数的日志）：",
  "adminlogs.cleanSuccess": "已清理 {n} 条历史日志。",
  "adminlogs.searchPh": "按用户、IP、操作搜索...",
  "adminlogs.allActions": "所有操作",
  "adminlogs.allStatuses": "所有状态",
  "adminlogs.paginationInfo": "共 {total} 条记录 | 第 {page} / {totalPages} 页",
  "adminlogs.invalidDays": "天数无效",
  "adminlogs.description": "描述",
  "adminlogs.request": "请求 (Request)",
  "action.auth.login": "登录 CMS 系统",
  "action.auth.logout": "退出 CMS",
  "action.auth.register": "注册用户账号",
  "action.brand-links.create": "添加监控链接",
  "action.brand-links.update": "更新监控链接",
  "action.brand-links.delete": "删除监控链接",
  "action.brand-links.restore": "恢复监控链接",
  "action.brand-links.check": "单独检查链接",
  "action.brand-links.whois": "检查域名 WHOIS 到期日",
  "action.brand-links.import": "批量导入链接 (JSON/CSV)",
  "action.brand-links.import-file": "从文件导入链接 (Excel/CSV)",
  "action.brand-links.bulk-delete": "批量删除监控链接",
  "action.brand-links.check-all": "立即检查全部链接",
  "action.brand-links.whois-all": "扫描全部域名 WHOIS 到期日",
  "action.brand-links.change-owner": "转移链接所有权",
  "action.reports.generate": "生成全面扫描报告",
  "action.devices.create": "注册新检查设备",
  "action.devices.update": "更新检查设备信息",
  "action.devices.delete": "删除检查设备",
  "action.devices.rotate-key": "重新生成设备 API 密钥",
  "action.device-checks.clear": "清理设备检查历史",
  "action.device-checks.send-alert": "发送设备异常告警",
  "action.brands.create": "创建新品牌",
  "action.brands.update": "更新品牌名称",
  "action.brands.delete": "删除品牌",
  "action.users.create": "创建新管理员账号",
  "action.users.update": "更新用户账号",
  "action.users.delete": "删除用户账号",
  "action.users.change-password": "修改用户密码",
  "action.channels.create": "创建新通知渠道",
  "action.channels.update": "更新通知渠道",
  "action.channels.delete": "删除通知渠道",
  "action.channels.test": "测试发送通知",
  "action.channels.clone": "复制渠道到其他国家",
  "action.roles.create": "创建新角色",
  "action.roles.update": "更新角色权限",
  "action.roles.delete": "删除角色",
  "action.ip-rules.create": "添加 CMS IP 规则",
  "action.ip-rules.update": "更新 CMS IP 规则",
  "action.ip-rules.delete": "删除 CMS IP 规则",
  "action.security.auto-ban-ip": "自动封禁可疑攻击 IP",
  "action.app-config.update": "更新应用配置",
  "action.app-config.upload-apk": "上传新版 APK",
  "action.admin-logs.clean": "清理历史操作日志",
  "action.notifications.read": "标记通知为已读",
  "action.notifications.read-all": "标记全部通知为已读",

  "error.sessionExpired": "登录已过期，请重新登录。",
  "error.generic": "错误 {status}",
  "error.download": "无法下载文件（{status}）",
  "error.badRequest": "提交的数据无效。",
  "error.invalidCredentials": "用户名或密码错误。",
  "error.forbidden": "您没有执行此操作的权限。",
  "error.notFound": "未找到数据。",
  "error.conflict": "数据重复。",
  "error.tooManyRequests": "请求过于频繁，请稍后再试。",
  "error.server": "服务器出现问题，请稍后重试。",
  "error.network": "无法连接到服务器。",
  "error.brandEmpty": "品牌为空",
  "error.domainInvalid": "域名无效",
  "error.linkNotFound": "链接不存在",
  "error.notOwner": "该链接属于其他用户 — 你只能编辑/删除自己的链接",
};

const DICTS: Record<Lang, Record<MessageKey, string>> = { vi, en, zh };

export const LANGS: { code: Lang; label: string }[] = [
  { code: "vi", label: "Tiếng Việt" },
  { code: "en", label: "English" },
  { code: "zh", label: "中文" },
];

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translate;
  /** Ngày giờ theo locale đang chọn. */
  fmtDate: (iso: string | null | undefined) => string;
  /** Khoảng cách thời gian dạng "3 phút trước". */
  fmtAgo: (iso: string | null | undefined) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function detectLang(): Lang {
  const saved = localStorage.getItem(LANG_KEY);
  if (saved && saved in DICTS) return saved as Lang;
  const nav = navigator.language.toLowerCase();
  if (nav.startsWith("vi")) return "vi";
  if (nav.startsWith("zh")) return "zh";
  return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const setLang = useCallback((next: Lang) => {
    localStorage.setItem(LANG_KEY, next);
    document.documentElement.lang = next;
    setLangState(next);
  }, []);

  const value = useMemo<I18nValue>(() => {
    const dict = DICTS[lang];
    const locale = { vi: "vi-VN", en: "en-GB", zh: "zh-CN" }[lang];

    const t: Translate = (key, vars) => {
      let text: string = dict[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          text = text.replaceAll(`{${k}}`, String(v));
        }
      }
      return text;
    };

    const fmtDate = (iso: string | null | undefined) => {
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const fmtAgo = (iso: string | null | undefined) => {
      if (!iso) return "—";
      const then = new Date(iso).getTime();
      if (Number.isNaN(then)) return "—";
      const mins = Math.round((Date.now() - then) / 60000);
      if (mins < 1) return t("time.justNow");
      if (mins < 60) return t("time.minutes", { n: mins });
      const hours = Math.round(mins / 60);
      if (hours < 24) return t("time.hours", { n: hours });
      return t("time.days", { n: Math.round(hours / 24) });
    };

    return { lang, setLang, t, fmtDate, fmtAgo };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n phải được dùng bên trong <I18nProvider>.");
  return ctx;
}

/** Bộ chọn ngôn ngữ dùng chung cho sidebar và màn hình đăng nhập. */
export function LangSwitch({ fullWidth = true }: { fullWidth?: boolean }) {
  const { lang, setLang } = useI18n();
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={lang}
      onChange={(_e, next) => next && setLang(next as Lang)}
      aria-label="Language"
      fullWidth={fullWidth}
    >
      {LANGS.map(l => (
        <ToggleButton key={l.code} value={l.code} sx={{ fontSize: 11.5, letterSpacing: "0.04em" }}>
          {l.code.toUpperCase()}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
